var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react';
import { getItemImages } from '../../api/item-images';
import ConditionEditor from './ConditionEditor';
import './ConditionEditor.scss';
import { normalizeImageUrl } from './lib/normalizeImageUrl';
import { buildSnapshot } from './lib/buildSnapshot';
import { jsonDeepEqual } from './lib/jsonDeepEqual';
import { NpcNode, NPCsContext } from './nodes/NpcNode';
import { PlayerResponseNode } from './nodes/PlayerResponseNode';
import { ResponseNode } from './nodes/ResponseNode';
import { EventNode } from './nodes/EventNode';
import { DialogueGroupNode } from './nodes/DialogueGroupNode';
import { v4 as uuidv4 } from 'uuid';
import ReactFlow, { Background, Controls, applyNodeChanges, applyEdgeChanges, addEdge, useUpdateNodeInternals } from 'reactflow';
import { useImmer } from 'use-immer';
import { FaPlus, FaTrash, FaComments, FaExclamationTriangle, FaChevronLeft, FaChevronRight, FaBolt, FaUser, FaComment, FaDesktop, FaBox, FaBullseye, FaBell, FaCogs, // For ux elements
FaGamepad, // For game events
FaSearch, // For search
FaTimes, // For clearing
FaImage, // For images
FaLink, // For dependency groups – scenarios
FaMap, // For dependency groups – map nodes
FaMapMarkerAlt, // For Map Point Trigger
FaHeartbeat, // For Health Status
FaClock, // For Time Trigger
FaChevronDown // For dropdown arrow
 } from 'react-icons/fa';
import ImageDropzone from '../ImageDropzone/ImageDropzone';
import { DialogueRewardLevel, createDefaultDialogue, PrizeType, // Add PrizeType import
ABILITY_COLORS, DialogueBackgroundMode } from '../../api/models';
import { getLevelDialogues, createDialogue, updateDialogue, getDialogueById } from '../../api/dialogues';
import ButtonEdge from './ButtonEdge';
import './ScenarioEditor.scss';
import { UXPanelNode, UXNotificationNode, ItemRewardNode, ItemRequirementNode, ItemSpentNode, TriggerNode, LevelIdContext, LevelNodesContext } from './NodeTypes';
import { getLevelEvents, updateEvent } from 'src/api/level-events'; // Add updateEvent
import { AvatarAbility, backendUrl } from '@common/contracts';
import MapPickerModal from '../MapPickerModal/MapPickerModal';
import { getNPCs } from '../../api/npcs'; // Add getNPCs import
import { determineRootNodeId } from '../../utils/dialogueUtils';
import { createScenario, getScenarioById } from '../../api/scenarios';
import { persistScenario as persistScenarioToServer } from '../../services/scenarioMutations';
import { EventBus } from '../../utils/EventBus';
import { getDialogueImages } from '../../api/dialogue-images';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import { getLevelScenarios } from '../../api/scenarios';
import { getLevelById } from '../../api/levels';
// Pull in connection-group styles from EventEditor so the modal looks consistent
import '../EventEditor/EventEditor.scss';
import { getLevelItems } from '../../api/items';
import { getMarketItems } from '../../api/market-items';
// Node type registration
const nodeTypes = {
    npc: NpcNode,
    response: ResponseNode,
    'player-response': PlayerResponseNode,
    event: EventNode,
    'ux-panel': UXPanelNode,
    'ux-notification': UXNotificationNode,
    'item-reward': ItemRewardNode,
    'item-requirement': ItemRequirementNode,
    'item-spent': ItemSpentNode,
    'trigger': TriggerNode,
    'dialogue-group': DialogueGroupNode
};
// Edge type registration
const edgeTypes = {
    buttonedge: ButtonEdge,
};
/**
 * Helper component that displays a message in the properties panel when a dialogue is selected but no specific node on the canvas is selected.
 */
const EmptyPropertiesState = () => (_jsxs("div", { className: "empty-properties", children: [_jsx(FaComments, {}), _jsx("p", { children: "Select a node from the dialogue flow or choose a category from the toolbar above to get started." }), _jsx("p", { children: "You can add new dialogue nodes, events, UX panels, items, or triggers using the category buttons." })] }));
/**
 * Helper component that displays a loading indicator.
 */
const LoadingState = () => (_jsxs("div", { className: "loading-screen", children: [_jsx("div", { className: "spinner" }), _jsx("p", { children: "Loading dialogues..." })] }));
const ErrorState = ({ message }) => (_jsxs("div", { className: "error-screen", children: [_jsx(FaExclamationTriangle, {}), _jsx("p", { children: message })] }));
/**
 * The `ScenarioEditor` component is the central part of the user interface for editing game scenarios.
 *
 * The main functional component that brings all the other parts together. It manages the editor state
 * (dialogue list, selected dialogue, selected node, nodes and edges for ReactFlow, loading/error state, panel visibility,
 * selected category). It loads data from the backend on initialization, converts dialogue data into the node and edge format
 * for `reactflow`, contains event handlers for interacting with the graph (dragging nodes, changing connections, clicks),
 * handles changes in the properties panel and interaction with the category panel for adding new elements. It uses
 * `useImmer` for convenient management of the nested node and edge state. It embeds `ReactFlow` as the main visualization component.
 *
 * This editor visualizes scenarios as a graph (diagram) with nodes and connections between them, using the `reactflow` library.
 *  It allows the user to:
 *  1. Select and load existing dialogues.
 *  2. Visualize the selected dialogue and other scenario elements (events, UX panels, items, triggers) as graph nodes.
 *  3. Edit the properties of selected nodes through the side panel.
 *  4. Add new nodes of various types (NPC dialogues, player responses, events, UX elements, items, triggers) onto the canvas either
 *     by dragging from the toolbar or by clicking elements in the properties panel.
 *  5. Establish connections between nodes by dragging the handles.
 *  6. Delete nodes and connections.
 *  7. Toggle the visibility of the side properties panel.
 *  8. Manage dialogue data (adding/removing NPC response options).
 *  9. Interact with the API to load data (dialogues, events) and potentially save changes.
 *
 * In essence, this is a comprehensive visual tool for creating and modifying interactive, scenario-based game elements.
 */
// Node size constants - centralized for the whole component
const NODE_WIDTH = 340; // approximate card width (visual)
const NODE_HEIGHT = 800; // card height close to iPhone-12 mock + answers
const H_GAP = 200; // horizontal gap between sibling nodes (increased)
const V_GAP = 200; // vertical gap between rows
function ScenarioEditor({ levelId, scenarioId, onBack, onScenarioSaved, onUnsavedChangesChange, onRegisterSave }) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    // State for saving scenario
    const [isSavingScenario, setIsSavingScenario] = useState(false);
    const [scenarioError, setScenarioError] = useState(null);
    // State for the dialogue list
    const [dialogues, updateDialogues] = useImmer([]);
    const [, setSelectedDialogueId] = useState(null);
    const [dialogueImages, setDialogueImages] = useState([]);
    // State for the current dialogue
    const [currentDialogue, updateCurrentDialogue] = useImmer(null);
    // State to hold the React Flow instance (needed to correctly compute coordinates during DnD)
    const [reactFlowInstance, setReactFlowInstance] = useState(null);
    // State for editing the selected node
    const [selectedNode, setSelectedNode] = useState(null);
    // Add state for the highlighted condition group
    const [selectedConditionGroupId, setSelectedConditionGroupId] = useState(null);
    const [selectedConditionId, setSelectedConditionId] = useState(null); // For a specific condition
    // Refs for scrolling
    const conditionGroupRefs = useRef({});
    const conditionItemRefs = useRef({});
    // Ref to access the current selectedNode in callbacks
    const selectedNodeRef = useRef(null);
    useEffect(() => {
        selectedNodeRef.current = selectedNode;
    }, [selectedNode]);
    // State for nodes and edges in ReactFlow
    const [nodes, updateNodes] = useImmer([]);
    const [edges, updateEdges] = useImmer([]);
    // --- helper to ensure edge connectionType stays actual ---
    const recomputeEdgeTypes = useCallback(() => {
        updateEdges(prev => {
            let changed = false;
            const mapped = prev.map(ed => {
                var _a, _b, _c, _d;
                // If sourceHandle is missing (old edge) — try to restore it.
                if (!ed.sourceHandle || !ed.sourceHandle.startsWith('response-')) {
                    const srcNode = nodes.find(n => n.id === ed.source && n.type === 'npc');
                    const tgtNode = nodes.find(n => n.id === ed.target && n.type === 'npc');
                    if (srcNode && tgtNode) {
                        const dlg = dialogues.find(d => d.id === srcNode.data.dialogueId);
                        const dlgNode = dlg === null || dlg === void 0 ? void 0 : dlg.nodes.find(n => n.id === srcNode.data.nodeId);
                        const opt = (_a = dlgNode === null || dlgNode === void 0 ? void 0 : dlgNode.responseOptions) === null || _a === void 0 ? void 0 : _a.find(o => {
                            const link = o.linkedToDialogueId;
                            return o.nextDialogueId === tgtNode.data.nodeId || link === tgtNode.data.nodeId;
                        });
                        if (opt) {
                            const newEd = Object.assign(Object.assign({}, ed), { sourceHandle: `response-${opt.id}` });
                            changed = true;
                            ed = newEd;
                        }
                    }
                }
                if (!ed.sourceHandle || !ed.sourceHandle.startsWith('response-'))
                    return ed;
                // External links: if the target is not an NPC – remove connectionType and exit
                const targetNodeTmp = nodes.find(n => n.id === ed.target);
                const srcNodeTmp = nodes.find(n => n.id === ed.source);
                // External link: target is not an NPC or the nodes belong to different dialogues – remove connectionType
                if (!targetNodeTmp || targetNodeTmp.type !== 'npc' || !srcNodeTmp || srcNodeTmp.data.dialogueId !== targetNodeTmp.data.dialogueId) {
                    if ((_b = ed.data) === null || _b === void 0 ? void 0 : _b.connectionType) {
                        const newData = Object.assign({}, ed.data);
                        delete newData.connectionType;
                        changed = true;
                        return Object.assign(Object.assign({}, ed), { data: newData });
                    }
                    return ed;
                }
                const optionId = ed.sourceHandle.replace('response-', '');
                const src = srcNodeTmp; // already found above
                if (!src)
                    return ed;
                const dlg = dialogues.find(d => d.id === src.data.dialogueId);
                if (!dlg)
                    return ed;
                const dlgNode = dlg.nodes.find(n => n.id === src.data.nodeId);
                if (!dlgNode)
                    return ed;
                const resp = (_c = dlgNode.responseOptions) === null || _c === void 0 ? void 0 : _c.find(o => o.id === optionId);
                if (!resp)
                    return ed;
                const isLink = resp.linkedToDialogueId !== undefined && resp.linkedToDialogueId !== null;
                const newType = isLink ? 'link' : 'nested';
                const currentType = (_d = ed.data) === null || _d === void 0 ? void 0 : _d.connectionType;
                if (currentType === newType)
                    return ed;
                changed = true;
                return Object.assign(Object.assign({}, ed), { data: Object.assign(Object.assign({}, (ed.data || {})), { connectionType: newType }) });
            });
            return changed ? mapped : prev;
        });
    }, [nodes, dialogues, updateEdges]);
    // Loading state
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    // State for filtering events in the right panel
    const [eventsSearchTerm, setEventsSearchTerm] = useState('');
    const [selectedEventCharacteristics, setSelectedEventCharacteristics] = useState([]);
    // State for filtering dialogues in the right panel
    const [dialoguesSearchTerm, setDialoguesSearchTerm] = useState('');
    const [selectedDialogueTypes, setSelectedDialogueTypes] = useState([]);
    // State for saving the scenario
    const [scenarioName, setScenarioName] = useState('');
    // State of the scenario save confirmation modal (must be before early returns)
    const [isScenarioSaveModalOpen, setIsScenarioSaveModalOpen] = useState(false);
    const [scenarioSaveMessage, setScenarioSaveMessage] = useState('');
    // Define the available abilities for filtering
    const availableCharacteristics = [
        AvatarAbility.Strength,
        AvatarAbility.Agility,
        AvatarAbility.Perception,
        AvatarAbility.Intellect,
        AvatarAbility.Defence
    ];
    // Define the available dialogue types for filtering
    const availableDialogueTypes = ['NPC', 'Interactive', 'Quest', 'Combat'];
    // Add state for showing/hiding the properties panel
    const [isPropertiesPanelVisible, setIsPropertiesPanelVisible] = useState(true);
    // Add state to hold the list of events
    const [events, setEvents] = useImmer([]);
    // --- level data for analyzing event dependencies ---
    const [scenarios, setScenarios] = useImmer([]);
    const [levelNodes, setLevelNodes] = useImmer({});
    const [levelData, setLevelData] = useState(null);
    const [, setEventsLoading] = useState(false);
    // State for NPCs
    const [availableNPCs, setAvailableNPCs] = useImmer([]);
    // --- Unsaved Event edit tracking ---
    const [, setHasUnsavedEventChanges] = useState(false);
    const [isUnsavedModalOpen, setIsUnsavedModalOpen] = useState(false);
    const [originalEventSnapshot, setOriginalEventSnapshot] = useState(null);
    // refs for pending actions while resolving unsaved edits
    const pendingSelectionRef = React.useRef(null);
    const pendingCategoryRef = React.useRef(null);
    const [selectedCategory, setSelectedCategory] = useState('dialogues');
    // Add state for items
    const [availableItems, setAvailableItems] = useState([]);
    const [, setItemsLoading] = useState(false);
    const [availableMarketItems, setAvailableMarketItems] = useState([]);
    // State for displaying the map thumbnail when selecting a Map Point
    const [isMapPickerOpen, setIsMapPickerOpen] = useState(false);
    // Add useUpdateNodeInternals to update nodes when their properties change
    const updateNodeInternals = useUpdateNodeInternals();
    // Handler to close the dropdown when clicking outside of it
    useEffect(() => {
        const handleClickOutside = (event) => {
            const target = event.target;
            if (!target.closest('.color-picker-dropdown') && !target.closest('.color-picker-trigger')) {
                // Close all open dropdowns
                document.querySelectorAll('.color-picker-dropdown').forEach(dropdown => {
                    dropdown.style.display = 'none';
                });
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);
    // Reset the category ONLY when a node is selected (NOT when selectedNode is reset to null)
    const prevSelectedNodeRef = useRef(null);
    useEffect(() => {
        // Reset the category only if selectedNode changed from null to something (a node was selected)
        if (selectedNode && !prevSelectedNodeRef.current) {
            setSelectedCategory(null);
        }
        prevSelectedNodeRef.current = selectedNode;
    }, [selectedNode]);
    // Clean up the animation frame when the component unmounts
    useEffect(() => {
        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);
    // Load the dialogue list on initialization
    useEffect(() => {
        const fetchDialogues = () => __awaiter(this, void 0, void 0, function* () {
            try {
                setIsLoading(true);
                // Clear the selected node when the level changes
                setSelectedNode(null);
                const dialoguesList = yield getLevelDialogues(levelId);
                updateDialogues(dialoguesList);
                // Do NOT automatically select the first dialogue - the user will choose
                setIsLoading(false);
                // Signal that the initial state is ready
                stateReadyRef.current = true;
            }
            catch (error) {
                console.error('Error loading dialogues:', error);
                setError('Failed to load dialogues. Please try again.');
                setIsLoading(false);
            }
        });
        fetchDialogues();
    }, [levelId, updateDialogues, updateCurrentDialogue]);
    // Load the list of events for the Events tab
    useEffect(() => {
        const fetchEvents = () => __awaiter(this, void 0, void 0, function* () {
            try {
                setEventsLoading(true);
                const eventsData = yield getLevelEvents(levelId);
                setEvents(eventsData);
                setEventsLoading(false);
            }
            catch (error) {
                console.error('Error loading events:', error);
                setEventsLoading(false);
            }
        });
        fetchEvents();
    }, [levelId, setEvents]);
    // --- Load level scenarios and map data ---
    useEffect(() => {
        getLevelScenarios(levelId)
            .then(data => setScenarios(data))
            .catch(err => console.error('Failed to load level scenarios', err));
        getLevelById(levelId)
            .then(level => {
            var _a;
            setLevelNodes(level.nodes || {});
            setLevelData({
                id: level.id,
                mapWidth: level.mapWidth,
                mapHeight: level.mapHeight,
                backgroundImage: (_a = level.backgroundImage) !== null && _a !== void 0 ? _a : undefined
            });
        })
            .catch(err => console.error('Failed to load level nodes', err));
    }, [levelId, setScenarios, setLevelNodes]);
    // Load NPCs for selection in dialogues
    useEffect(() => {
        const fetchNPCs = () => __awaiter(this, void 0, void 0, function* () {
            try {
                const npcs = yield getNPCs();
                const activeNPCs = npcs.filter(npc => !npc.isArchived);
                setAvailableNPCs(activeNPCs);
            }
            catch (error) {
                console.error('Error loading NPCs:', error);
            }
        });
        fetchNPCs();
    }, [setAvailableNPCs]);
    // Load items for the level
    useEffect(() => {
        const fetchItems = () => __awaiter(this, void 0, void 0, function* () {
            try {
                setItemsLoading(true);
                const items = yield getLevelItems(levelId);
                const activeItems = items.filter(item => !item.isArchived);
                setAvailableItems(activeItems);
                setItemsLoading(false);
            }
            catch (error) {
                console.error('Error loading items:', error);
                setItemsLoading(false);
            }
        });
        fetchItems();
    }, [levelId]);
    useEffect(() => {
        const fetchMarketItems = () => __awaiter(this, void 0, void 0, function* () {
            try {
                const marketItems = yield getMarketItems();
                const activeMarketItems = marketItems.filter(item => !item.isArchived);
                setAvailableMarketItems(activeMarketItems);
            }
            catch (error) {
                console.error('Error loading market items:', error);
            }
        });
        fetchMarketItems();
    }, []);
    // Load item images for the level
    const [itemImages, setItemImages] = useState([]);
    useEffect(() => {
        const fetchItemImages = () => __awaiter(this, void 0, void 0, function* () {
            try {
                const images = yield getItemImages(levelId);
                const activeImages = images.filter((img) => !img.isArchived);
                setItemImages(activeImages);
            }
            catch (error) {
                console.error('Error loading item images:', error);
            }
        });
        fetchItemImages();
    }, [levelId]);
    // -----------------------
    // Dialogue images support
    // -----------------------
    useEffect(() => {
        const fetchDialogueImages = () => __awaiter(this, void 0, void 0, function* () {
            try {
                const imgs = yield getDialogueImages(levelId);
                setDialogueImages(imgs);
            }
            catch (err) {
                console.error('Failed to load dialogue images', err);
            }
        });
        fetchDialogueImages();
    }, [levelId]);
    const getDialogueBackground = useCallback((dlg) => {
        if (dlg.backgroundMode === DialogueBackgroundMode.Custom && dlg.backgroundImageId) {
            const img = dialogueImages.find(i => i.id === dlg.backgroundImageId);
            return img === null || img === void 0 ? void 0 : img.imageUrl;
        }
        return undefined;
    }, [dialogueImages]);
    // Unified list of all elements for requirements
    const availableRequirements = useMemo(() => {
        const requirements = [];
        // Add regular items
        availableItems.forEach(item => {
            const itemImage = itemImages.find(img => img.id === item.imageId);
            requirements.push({
                id: item.id,
                name: item.name,
                type: 'item',
                imageUrl: itemImage === null || itemImage === void 0 ? void 0 : itemImage.imageUrl
            });
        });
        // Add market items
        availableMarketItems.forEach(marketItem => {
            requirements.push({
                id: String(marketItem.id),
                name: marketItem.name,
                type: 'market-item',
                imageUrl: marketItem.image || undefined,
                category: marketItem.category
            });
        });
        return requirements;
    }, [availableItems, availableMarketItems, itemImages]);
    // Load the data of the scenario being edited by ID
    useEffect(() => {
        if (!scenarioId)
            return;
        setIsLoading(true);
        const loadScenario = () => __awaiter(this, void 0, void 0, function* () {
            try {
                // Fetch the scenario and the current level dialogue list in parallel
                const [scenario, dlgList] = yield Promise.all([
                    getScenarioById(levelId, scenarioId),
                    getLevelDialogues(levelId)
                ]);
                if (!scenario)
                    return;
                // Build the set of current (non-archived) dialogues
                const validDialogueIds = new Set(dlgList.filter(d => !d.isArchived).map(d => d.id));
                // Step 1. Filter out nodes referencing non-existent/archived dialogues
                const removedNodeIds = [];
                const filteredNodes = scenario.nodes.filter((n) => {
                    var _a;
                    const dlgId = (_a = n === null || n === void 0 ? void 0 : n.data) === null || _a === void 0 ? void 0 : _a.dialogueId;
                    if (!dlgId || dlgId === 'standalone' || validDialogueIds.has(dlgId)) {
                        return true;
                    }
                    removedNodeIds.push(n.id);
                    return false;
                });
                // Clean up edges leading to removed nodes
                const removedSet = new Set(removedNodeIds);
                const filteredEdges = scenario.edges.filter((e) => !removedSet.has(e.source) && !removedSet.has(e.target));
                // If something changed — save the scenario back (migration).
                // Goes through the shared mutation service so the save is
                // visible on the bus like any other scenario write.
                if (filteredNodes.length !== scenario.nodes.length) {
                    const updatedScenario = Object.assign(Object.assign({}, scenario), { nodes: filteredNodes, edges: filteredEdges });
                    yield persistScenarioToServer(levelId, updatedScenario, ScenarioEditor.name);
                    // Use the already cleaned data
                    scenario.nodes = filteredNodes;
                    scenario.edges = filteredEdges;
                }
                setScenarioName(scenario.name);
                updateNodes(() => scenario.nodes);
                updateEdges(() => scenario.edges);
                // Update the ReactFlow cache for NPC nodes
                scenario.nodes.forEach((node) => {
                    if (node.type === 'npc') {
                        updateNodeInternals(node.id);
                    }
                });
                // All migrations and synchronization are complete – the data is ready
                stateReadyRef.current = true;
            }
            catch (error) {
                console.error('Error loading scenario:', error);
                setError('Failed to load scenario');
            }
        });
        loadScenario();
    }, [scenarioId, levelId, updateNodes, updateEdges, updateNodeInternals]);
    // Dialogue nodes are now created independently (like event nodes) and are not recreated in useEffect
    // Synchronization with the dialogue model happens through handleUpdateNode
    // Handlers for node and edge changes
    const onNodesChange = useCallback(changes => {
        updateNodes(nodes => applyNodeChanges(changes, nodes));
        // --- SYNCHRONIZE NODE DELETION WITH DIALOGUE DATA ---
        changes.forEach(ch => {
            if (ch.type !== 'remove')
                return;
            const removedNode = nodes.find(n => n.id === ch.id);
            if (!removedNode)
                return;
            const { data } = removedNode;
            // If we are deleting an NPC node, remove it from the dialogue model and clean up references
            if (data.type === 'npc') {
                updateDialogues(draft => {
                    const dialogue = draft.find(d => d.id === data.dialogueId);
                    if (!dialogue)
                        return;
                    // Remove the node itself
                    dialogue.nodes = dialogue.nodes.filter(n => n.id !== data.nodeId);
                    // Clear nextDialogueId references on all responses
                    dialogue.nodes.forEach(n => {
                        var _a;
                        (_a = n.responseOptions) === null || _a === void 0 ? void 0 : _a.forEach(opt => {
                            if (opt.nextDialogueId === data.nodeId) {
                                opt.nextDialogueId = undefined;
                            }
                        });
                    });
                });
                if (currentDialogue && currentDialogue.id === data.dialogueId) {
                    updateCurrentDialogue(draft => {
                        if (!draft)
                            return;
                        draft.nodes = draft.nodes.filter(n => n.id !== data.nodeId);
                        draft.nodes.forEach(n => {
                            var _a;
                            (_a = n.responseOptions) === null || _a === void 0 ? void 0 : _a.forEach(opt => {
                                if (opt.nextDialogueId === data.nodeId) {
                                    opt.nextDialogueId = undefined;
                                }
                            });
                        });
                    });
                }
            }
            // If we are deleting a standalone response node (player-response / response)
            if (data.type === 'response' || data.type === 'player-response') {
                updateDialogues(dialoguesDraft => {
                    var _a;
                    const dialogueDraft = dialoguesDraft.find(d => d.id === data.dialogueId);
                    if (!dialogueDraft)
                        return;
                    // Find the parent NPC node this response was attached to
                    const parentNpc = dialogueDraft.nodes.find(n => n.id === data.nodeId);
                    (_a = parentNpc === null || parentNpc === void 0 ? void 0 : parentNpc.responseOptions) === null || _a === void 0 ? void 0 : _a.forEach((opt, idx, arr) => {
                        if (`response-${opt.id}` === data.id) {
                            arr.splice(idx, 1);
                        }
                    });
                });
                if (currentDialogue && currentDialogue.id === data.dialogueId) {
                    updateCurrentDialogue(draft => {
                        var _a;
                        if (!draft)
                            return;
                        const parentNpc = draft.nodes.find(n => n.id === data.nodeId);
                        (_a = parentNpc === null || parentNpc === void 0 ? void 0 : parentNpc.responseOptions) === null || _a === void 0 ? void 0 : _a.forEach((opt, idx, arr) => {
                            if (`response-${opt.id}` === data.id) {
                                arr.splice(idx, 1);
                            }
                        });
                    });
                }
            }
            // Remove related edges
            updateEdges(draft => draft.filter(e => e.source !== removedNode.id && e.target !== removedNode.id));
            // --- RESET THE SELECTED NODE IF IT WAS DELETED ---
            if (selectedNodeRef.current && selectedNodeRef.current.id === removedNode.id) {
                setSelectedNode(null);
            }
            // --- AUTOMATIC RIGHT PANEL SWITCHING ---
            if (removedNode.type === 'dialogue-group') {
                setSelectedCategory('dialogues');
            }
            if (removedNode.type === 'event') {
                setSelectedCategory('events');
            }
        });
    }, [nodes, updateEdges, updateCurrentDialogue, updateNodes, setSelectedCategory]);
    // Function to update the bounds of a specific dialogue group taking the parent/child system into account
    const updateGroupBoundsForDialogue = useCallback((dialogueId, draggedNode, tempPosition, preservePosition = false) => {
        var _a, _b, _c, _d;
        const groupNodeId = `group_${dialogueId}`;
        // Find the dialogue nodes and the group
        const dialogueNodes = [];
        let groupNode = null;
        for (let i = 0; i < nodes.length; i++) {
            const node = nodes[i];
            if (node.id === groupNodeId) {
                groupNode = node;
            }
            else if (node.data.dialogueId === dialogueId && node.type !== 'dialogue-group') {
                if (draggedNode && node.id === draggedNode.id && tempPosition) {
                    dialogueNodes.push({ x: tempPosition.x, y: tempPosition.y, node });
                }
                else {
                    // Compute the absolute position of the node
                    let absX = node.position.x;
                    let absY = node.position.y;
                    // If the node has a parentId, its position is relative to the parent
                    if (node.parentId === groupNodeId && groupNode) {
                        absX = groupNode.position.x + node.position.x;
                        absY = groupNode.position.y + node.position.y;
                    }
                    dialogueNodes.push({ x: absX, y: absY, node });
                }
            }
        }
        if (groupNode && dialogueNodes.length > 0) {
            // Quick bounds calculation in absolute coordinates
            let minX = dialogueNodes[0].x;
            let minY = dialogueNodes[0].y;
            let maxX = dialogueNodes[0].x + NODE_WIDTH;
            let maxY = dialogueNodes[0].y + NODE_HEIGHT;
            for (let i = 1; i < dialogueNodes.length; i++) {
                const pos = dialogueNodes[i];
                minX = Math.min(minX, pos.x);
                minY = Math.min(minY, pos.y);
                maxX = Math.max(maxX, pos.x + NODE_WIDTH);
                maxY = Math.max(maxY, pos.y + NODE_HEIGHT);
            }
            const contentWidth = maxX - minX;
            const contentHeight = maxY - minY;
            const paddingX = Math.max(30, contentWidth * 0.15);
            const paddingY = Math.max(30, contentHeight * 0.15);
            const newGroupX = minX - paddingX;
            const newGroupY = minY - paddingY;
            const newGroupWidth = contentWidth + (paddingX * 2);
            const newGroupHeight = contentHeight + (paddingY * 2);
            // Final values for the group
            let finalGroupX = newGroupX;
            let finalGroupY = newGroupY;
            let finalGroupWidth = newGroupWidth;
            let finalGroupHeight = newGroupHeight;
            // If preservePosition = true, do NOT move the group, only resize it
            if (preservePosition) {
                const currentGroupX = groupNode.position.x;
                const currentGroupY = groupNode.position.y;
                // Expand the group so that all nodes fit, but do NOT move it
                finalGroupX = Math.min(currentGroupX, minX - paddingX);
                finalGroupY = Math.min(currentGroupY, minY - paddingY);
                // Recompute the size taking the new position into account
                const rightBound = Math.max(currentGroupX + (((_a = groupNode.style) === null || _a === void 0 ? void 0 : _a.width) || 0), maxX + paddingX);
                const bottomBound = Math.max(currentGroupY + (((_b = groupNode.style) === null || _b === void 0 ? void 0 : _b.height) || 0), maxY + paddingY);
                // Use the recomputed size
                finalGroupWidth = Math.max(rightBound - finalGroupX, contentWidth + (paddingX * 2));
                finalGroupHeight = Math.max(bottomBound - finalGroupY, contentHeight + (paddingY * 2));
            }
            // Update only if the size or position actually changed
            const needsUpdate = Math.abs(groupNode.position.x - finalGroupX) > 1 ||
                Math.abs(groupNode.position.y - finalGroupY) > 1 ||
                Math.abs((((_c = groupNode.style) === null || _c === void 0 ? void 0 : _c.width) || 0) - finalGroupWidth) > 1 ||
                Math.abs((((_d = groupNode.style) === null || _d === void 0 ? void 0 : _d.height) || 0) - finalGroupHeight) > 1;
            if (needsUpdate) {
                const deltaX = finalGroupX - groupNode.position.x;
                const deltaY = finalGroupY - groupNode.position.y;
                updateNodes(draft => {
                    const groupIndex = draft.findIndex(n => n.id === groupNodeId);
                    if (groupIndex >= 0) {
                        // Update the group size and position
                        draft[groupIndex] = Object.assign(Object.assign({}, draft[groupIndex]), { position: { x: finalGroupX, y: finalGroupY }, data: Object.assign(Object.assign({}, draft[groupIndex].data), { groupChildrenCount: dialogueNodes.length, groupDialogueName: draft[groupIndex].data.groupDialogueName // keep the existing name
                             }), style: Object.assign(Object.assign({}, draft[groupIndex].style), { width: finalGroupWidth, height: finalGroupHeight, zIndex: -1 }) });
                        // In preservePosition mode do NOT adjust child node positions
                        if (!preservePosition) {
                            // Adjust relative positions of child nodes only in normal mode
                            draft.forEach(node => {
                                if (node.data.dialogueId === dialogueId &&
                                    node.type !== 'dialogue-group' &&
                                    node.parentId === groupNodeId) {
                                    node.position = {
                                        x: node.position.x - deltaX,
                                        y: node.position.y - deltaY
                                    };
                                }
                            });
                        }
                    }
                });
            }
        }
    }, [nodes, updateNodes]);
    // Track deletion of dialogue nodes to update group bounds
    const prevNodesLengthRef = useRef(0);
    const prevDialogueNodesRef = useRef(new Map());
    useEffect(() => {
        // Track changes in the number of dialogue nodes
        const currentDialogueNodes = new Map();
        nodes.forEach(node => {
            if (node.type !== 'dialogue-group' && node.data.dialogueId && node.data.dialogueId !== 'standalone') {
                const dlgId = node.data.dialogueId;
                currentDialogueNodes.set(dlgId, (currentDialogueNodes.get(dlgId) || 0) + 1);
            }
        });
        // Compare with the previous state
        const prevDialogueNodes = prevDialogueNodesRef.current;
        // Find dialogues where the node count decreased (deletion)
        for (const [dlgId, prevCount] of prevDialogueNodes.entries()) {
            const currentCount = currentDialogueNodes.get(dlgId) || 0;
            if (currentCount < prevCount && currentCount > 0) {
                // Nodes were deleted, but the dialogue is not empty - update bounds with preservePosition = true
                setTimeout(() => {
                    updateGroupBoundsForDialogue(dlgId, undefined, undefined, true);
                }, 10);
            }
        }
        // Save the current state for the next comparison
        prevNodesLengthRef.current = nodes.length;
        prevDialogueNodesRef.current = currentDialogueNodes;
    }, [nodes, updateGroupBoundsForDialogue]);
    // Reference to requestAnimationFrame for smooth updates
    const animationFrameRef = useRef(null);
    // Real-time node drag handler - disabled to avoid conflicts
    const onNodeDrag = useCallback((_event, _node, _nodes) => {
        // Do not update group bounds during dragging - this conflicts with the parent/child system
    }, []);
    // Node drag end handler (kept for the final update)
    const onNodeDragStop = useCallback((_event, node, _nodes) => {
        // Check whether the dragged node is part of a dialogue (but not a group)
        if (node.data.dialogueId && node.type !== 'dialogue-group') {
            // Final group bounds update
            updateGroupBoundsForDialogue(node.data.dialogueId);
        }
    }, [updateGroupBoundsForDialogue]);
    const onEdgesChange = useCallback(changes => {
        updateEdges(draft => applyEdgeChanges(changes, draft));
        // 1. Synchronize the dialogue model when edges are removed
        changes.forEach(ch => {
            if (ch.type === 'remove') {
                const removedEdge = edges.find(e => e.id === ch.id);
                if (!removedEdge || !removedEdge.sourceHandle)
                    return;
                // Determine optionId if sourceHandle is in the response-<id> format
                const optionId = removedEdge.sourceHandle && removedEdge.sourceHandle.startsWith('response-')
                    ? removedEdge.sourceHandle.replace('response-', '')
                    : null;
                // Find the source NPC Flow node
                const srcNode = nodes.find(n => n.id === removedEdge.source);
                if (!srcNode)
                    return;
                // Update the dialogues array
                updateDialogues(draft => {
                    var _a, _b;
                    const dlg = draft.find(d => d.id === srcNode.data.dialogueId);
                    if (!dlg)
                        return;
                    const dlgNode = dlg.nodes.find(n => n.id === srcNode.data.nodeId);
                    if (!dlgNode)
                        return;
                    const resp = optionId ? (_a = dlgNode.responseOptions) === null || _a === void 0 ? void 0 : _a.find(r => r.id === optionId) : undefined;
                    if (optionId ? resp : true) {
                        const targetFlowNode = nodes.find(n => n.id === removedEdge.target);
                        const targetDlgNodeId = (_b = targetFlowNode === null || targetFlowNode === void 0 ? void 0 : targetFlowNode.data) === null || _b === void 0 ? void 0 : _b.nodeId;
                        if (targetDlgNodeId) {
                            if ((resp === null || resp === void 0 ? void 0 : resp.nextDialogueId) === targetDlgNodeId) {
                                resp.nextDialogueId = undefined;
                            }
                            if (resp && resp.linkedToDialogueId === targetDlgNodeId) {
                                delete resp.linkedToDialogueId;
                            }
                            // After deletion, check whether a hierarchical connection to the target remains
                            const ensureNested = (draftDialogues) => {
                                let hasNested = false;
                                draftDialogues.forEach(d => {
                                    d.nodes.forEach(nn => {
                                        (nn.responseOptions || []).forEach(ro => {
                                            if (ro.nextDialogueId === targetDlgNodeId) {
                                                hasNested = true;
                                            }
                                        });
                                    });
                                });
                                if (!hasNested) {
                                    // Find the first link reference and turn it into nested
                                    for (const d of draftDialogues) {
                                        let done = false;
                                        d.nodes.forEach(nn => {
                                            (nn.responseOptions || []).forEach(ro => {
                                                if (!done && ro.linkedToDialogueId === targetDlgNodeId) {
                                                    ro.nextDialogueId = targetDlgNodeId;
                                                    delete ro.linkedToDialogueId;
                                                    done = true;
                                                }
                                            });
                                        });
                                        if (done)
                                            break;
                                    }
                                }
                            };
                            ensureNested(draft);
                        }
                    }
                });
                // If this is currentDialogue – update it as well
                if (currentDialogue && currentDialogue.id === srcNode.data.dialogueId) {
                    updateCurrentDialogue(draft => {
                        var _a, _b;
                        if (!draft)
                            return;
                        const dlgNode = draft.nodes.find(n => n.id === srcNode.data.nodeId);
                        const resp = optionId ? (_a = dlgNode === null || dlgNode === void 0 ? void 0 : dlgNode.responseOptions) === null || _a === void 0 ? void 0 : _a.find(r => r.id === optionId) : undefined;
                        if (optionId ? resp : true) {
                            const targetFlowNode = nodes.find(n => n.id === removedEdge.target);
                            const targetDlgNodeId = (_b = targetFlowNode === null || targetFlowNode === void 0 ? void 0 : targetFlowNode.data) === null || _b === void 0 ? void 0 : _b.nodeId;
                            if (targetDlgNodeId) {
                                if ((resp === null || resp === void 0 ? void 0 : resp.nextDialogueId) === targetDlgNodeId) {
                                    resp.nextDialogueId = undefined;
                                }
                                if (resp && resp.linkedToDialogueId === targetDlgNodeId) {
                                    delete resp.linkedToDialogueId;
                                }
                                // After deletion, check whether a hierarchical connection to the target remains
                                const ensureNested = (draftDialogues) => {
                                    let hasNested = false;
                                    draftDialogues.forEach(d => {
                                        d.nodes.forEach(nn => {
                                            (nn.responseOptions || []).forEach(ro => {
                                                if (ro.nextDialogueId === targetDlgNodeId) {
                                                    hasNested = true;
                                                }
                                            });
                                        });
                                    });
                                    if (!hasNested) {
                                        // Find the first link reference and turn it into nested
                                        for (const d of draftDialogues) {
                                            let done = false;
                                            d.nodes.forEach(nn => {
                                                (nn.responseOptions || []).forEach(ro => {
                                                    if (!done && ro.linkedToDialogueId === targetDlgNodeId) {
                                                        ro.nextDialogueId = targetDlgNodeId;
                                                        delete ro.linkedToDialogueId;
                                                        done = true;
                                                    }
                                                });
                                            });
                                            if (done)
                                                break;
                                        }
                                    }
                                };
                                ensureNested([draft]);
                            }
                        }
                    });
                }
            }
        });
        // 2. Force an update of all NPC nodes (to refresh handle activity)
        updateNodes(draft => {
            draft.forEach(node => {
                if (node.type === 'npc') {
                    node.data = Object.assign(Object.assign({}, node.data), { _forceUpdate: Date.now() });
                }
            });
        });
        // Ask ReactFlow to recompute dynamic handles if their count changed
        nodes.forEach(n => {
            if (n.type === 'npc' || n.type === 'response' || n.type === 'player-response') {
                updateNodeInternals(n.id);
            }
        });
        // --- RECOMPUTE THE ROOT NPC FOR EACH DIALOGUE ---
        const edgesSnapshot = [...edges];
        const dialogueRoots = new Map();
        // Count incoming edges for each NPC
        const incomingMap = new Map();
        edgesSnapshot.forEach(e => {
            if (!e.target)
                return;
            incomingMap.set(e.target, (incomingMap.get(e.target) || 0) + 1);
        });
        nodes.forEach(n => {
            var _a;
            if (n.type !== 'npc')
                return;
            const hasIncoming = ((_a = incomingMap.get(n.id)) !== null && _a !== void 0 ? _a : 0) > 0;
            if (!hasIncoming) {
                dialogueRoots.set(n.data.dialogueId, n.data.nodeId);
            }
        });
        // Update dialogues and currentDialogue
        dialogueRoots.forEach((rootNodeId, dlgId) => {
            updateDialogues(draft => {
                const d = draft.find(dg => dg.id === dlgId);
                if (d)
                    d.rootNodeId = rootNodeId;
            });
            if (currentDialogue && currentDialogue.id === dlgId) {
                updateCurrentDialogue(d => { if (d)
                    d.rootNodeId = rootNodeId; });
            }
        });
        // Update the isRoot flag on the nodes
        updateNodes(draft => {
            draft.forEach(n => {
                if (n.type === 'npc') {
                    const isRoot = dialogueRoots.get(n.data.dialogueId) === n.data.nodeId;
                    n.className = isRoot ? 'root-npc' : '';
                    n.data = Object.assign(Object.assign({}, n.data), { isRoot });
                }
            });
        });
        // After all data updates, immediately recompute the connection types
        // so that the SEQ/LINK badges reflect the current state without delay.
        recomputeEdgeTypes();
    }, [nodes, edges, updateEdges, updateCurrentDialogue, updateNodes, updateDialogues, updateNodeInternals, recomputeEdgeTypes]);
    const onConnect = useCallback((connection) => {
        var _a;
        // Function to choose the optimal target handle when creating a new connection
        const getOptimalTargetHandleForConnection = (sourceNodeId, targetNodeId) => {
            const sourceNode = nodes.find(n => n.id === sourceNodeId);
            const targetNode = nodes.find(n => n.id === targetNodeId);
            if (!sourceNode || !targetNode)
                return 'target-top';
            // For Event nodes always use the top input
            if (targetNode.type === 'event') {
                return 'target-top';
            }
            const deltaX = targetNode.position.x - sourceNode.position.x;
            // For other nodes – the left/right side input
            return deltaX >= 0 ? 'target-left' : 'target-right';
        };
        // Function to choose the optimal source handle for response nodes
        const getOptimalSourceHandleForConnection = (sourceNodeId, targetNodeId) => {
            const sourceNode = nodes.find(n => n.id === sourceNodeId);
            const targetNode = nodes.find(n => n.id === targetNodeId);
            if (!sourceNode || !targetNode)
                return connection.sourceHandle || 'source-bottom';
            // If a specific handle is already specified, use it
            if (connection.sourceHandle)
                return connection.sourceHandle;
            // For response nodes, choose the optimal handle based on position
            if (sourceNode.type === 'response' || sourceNode.type === 'player-response') {
                const deltaX = targetNode.position.x - sourceNode.position.x;
                const deltaY = targetNode.position.y - sourceNode.position.y;
                // If the target is significantly lower, use the bottom handle
                if (deltaY > 100)
                    return 'source-bottom';
                // Otherwise choose a side handle based on the horizontal offset
                return deltaX >= 0 ? 'source-right' : 'source-left';
            }
            return connection.sourceHandle || 'source-bottom';
        };
        // Determine the optimal handles
        const sourceHandle = getOptimalSourceHandleForConnection(connection.source, connection.target);
        const targetHandle = connection.targetHandle || getOptimalTargetHandleForConnection(connection.source, connection.target);
        // --- determine connectionType ONLY for internal "response → NPC" connections ---
        const targetNodeObj = nodes.find(n => n.id === connection.target);
        const edgeData = {};
        if (sourceHandle.startsWith('response-') &&
            (targetNodeObj === null || targetNodeObj === void 0 ? void 0 : targetNodeObj.type) === 'npc' &&
            ((_a = nodes.find(n => n.id === connection.source)) === null || _a === void 0 ? void 0 : _a.data.dialogueId) === targetNodeObj.data.dialogueId) {
            const hasNestedAlready = edges.some(e => { var _a; return e.target === connection.target && ((_a = e.data) === null || _a === void 0 ? void 0 : _a.connectionType) === 'nested'; });
            edgeData.connectionType = hasNestedAlready ? 'link' : 'nested';
        }
        // Add a new edge of type buttonedge
        updateEdges(draft => addEdge(Object.assign(Object.assign({}, connection), { sourceHandle,
            targetHandle, type: 'buttonedge', data: edgeData }), draft));
        // Force an update of all NPC node data to re-render with the new handle logic
        updateNodes(draft => {
            draft.forEach(node => {
                if (node.type === 'npc') {
                    // Trigger a re-render by adding a service field
                    node.data = Object.assign(Object.assign({}, node.data), { _forceUpdate: Date.now() });
                }
            });
        });
        // Update the dialogue data model
        // Find the source node (response) and the target node (npc)
        const sourceNode = nodes.find(node => node.id === connection.source);
        const targetNode = nodes.find(node => node.id === connection.target);
        if ((sourceNode === null || sourceNode === void 0 ? void 0 : sourceNode.data.type) === 'response' && (targetNode === null || targetNode === void 0 ? void 0 : targetNode.data.type) === 'npc') {
            updateCurrentDialogue(draft => {
                var _a;
                if (draft) {
                    // Find the relevant dialogue node
                    const dialogueNode = draft.nodes.find(node => node.id === sourceNode.data.nodeId);
                    if (dialogueNode) {
                        // Find the relevant response option
                        const responseOption = (_a = dialogueNode.responseOptions) === null || _a === void 0 ? void 0 : _a.find(option => `response-${option.id}` === sourceNode.id);
                        if (responseOption) {
                            // Set the next dialogue node for this response option
                            responseOption.nextDialogueId = targetNode.data.nodeId;
                            delete responseOption.linkedToDialogueId;
                        }
                    }
                }
            });
            // Also record it in the general dialogues list
            updateDialogues(draft => {
                var _a;
                const dlg = draft.find(d => d.id === sourceNode.data.dialogueId);
                if (!dlg)
                    return;
                const dlgNode = dlg.nodes.find(n => n.id === sourceNode.data.nodeId);
                if (!dlgNode)
                    return;
                const resp = (_a = dlgNode.responseOptions) === null || _a === void 0 ? void 0 : _a.find(o => `response-${o.id}` === sourceNode.id);
                if (resp) {
                    resp.nextDialogueId = targetNode.data.nodeId;
                    delete resp.linkedToDialogueId;
                }
            });
        }
        // We append below existing updateDialogues block inside onConnect
        else if ((sourceNode === null || sourceNode === void 0 ? void 0 : sourceNode.data.type) === 'npc' && connection.sourceHandle && connection.sourceHandle.startsWith('response-') && (targetNode === null || targetNode === void 0 ? void 0 : targetNode.data.type) === 'npc') {
            const optionId = connection.sourceHandle.replace('response-', '');
            // Check whether a tree connection to the target node already exists
            const hasNested = edges.some(e => { var _a; return e.target === targetNode.id && ((_a = e.data) === null || _a === void 0 ? void 0 : _a.connectionType) === 'nested'; });
            const applyUpdate = (dlg) => {
                var _a;
                if (!dlg)
                    return;
                const srcDlgNode = dlg.nodes.find(n => n.id === sourceNode.data.nodeId);
                if (!srcDlgNode)
                    return;
                const respOpt = (_a = srcDlgNode.responseOptions) === null || _a === void 0 ? void 0 : _a.find(o => o.id === optionId);
                if (!respOpt)
                    return;
                if (!hasNested) {
                    respOpt.nextDialogueId = targetNode.data.nodeId;
                    delete respOpt.linkedToDialogueId;
                }
                else {
                    respOpt.linkedToDialogueId = targetNode.data.nodeId;
                }
            };
            updateCurrentDialogue(draft => {
                if (draft)
                    applyUpdate(draft);
            });
            updateDialogues(draft => { const d = draft.find(di => di.id === sourceNode.data.dialogueId); applyUpdate(d); });
        }
        // Recompute the types after adding a new edge
        recomputeEdgeTypes();
    }, [nodes, updateEdges, updateCurrentDialogue, updateNodes, updateDialogues, recomputeEdgeTypes, selectedNode]);
    // --- Recompute root NPC nodes ---
    useEffect(() => {
        if (nodes.length === 0)
            return;
        const incoming = new Map();
        edges.forEach(e => {
            var _a;
            if (e.target)
                incoming.set(e.target, ((_a = incoming.get(e.target)) !== null && _a !== void 0 ? _a : 0) + 1);
        });
        const roots = new Map();
        nodes.forEach(n => {
            var _a;
            if (n.type !== 'npc')
                return;
            const hasIncoming = ((_a = incoming.get(n.id)) !== null && _a !== void 0 ? _a : 0) > 0;
            if (!hasIncoming && !roots.has(n.data.dialogueId)) {
                roots.set(n.data.dialogueId, n.data.nodeId);
            }
        });
        if (roots.size === 0)
            return;
        updateDialogues(draft => {
            roots.forEach((rootId, dlgId) => {
                const dlg = draft.find(d => d.id === dlgId);
                if (dlg && dlg.rootNodeId !== rootId)
                    dlg.rootNodeId = rootId;
            });
        });
        if (currentDialogue && roots.has(currentDialogue.id)) {
            const newRoot = roots.get(currentDialogue.id);
            if (currentDialogue.rootNodeId !== newRoot) {
                updateCurrentDialogue(d => { if (d)
                    d.rootNodeId = newRoot; });
            }
        }
        updateNodes(draft => {
            draft.forEach(n => {
                if (n.type !== 'npc')
                    return;
                const isRootNow = roots.get(n.data.dialogueId) === n.data.nodeId;
                if (n.data.isRoot !== isRootNow) {
                    n.data = Object.assign(Object.assign({}, n.data), { isRoot: isRootNow, _forceUpdate: Date.now() });
                }
            });
        });
    }, [nodes, edges, updateDialogues, updateNodes, updateCurrentDialogue, currentDialogue]);
    // Node selection handler
    const proceedPendingAction = useCallback(() => {
        // Perform the deferred selectNode / category switch, if any
        if (pendingSelectionRef.current) {
            setSelectedNode(pendingSelectionRef.current.data);
            pendingSelectionRef.current = null;
        }
        if (pendingCategoryRef.current !== null) {
            setSelectedCategory(prev => prev === pendingCategoryRef.current ? null : pendingCategoryRef.current);
            pendingCategoryRef.current = null;
        }
    }, [setSelectedCategory]);
    const onNodeClick = useCallback((_event, node) => {
        var _a;
        // When a new Event node is selected, save a snapshot of its original data
        if (((_a = node.data) === null || _a === void 0 ? void 0 : _a.type) === 'event' && node.data.eventData) {
            setOriginalEventSnapshot(JSON.parse(JSON.stringify(node.data.eventData)));
            setHasUnsavedEventChanges(false);
        }
        setSelectedNode(node.data);
        // Reset condition group selection when the node changes
        setSelectedConditionGroupId(null);
        // Reset specific condition selection when the node changes
        setSelectedConditionId(null);
    }, [selectedNode]);
    // Function to scroll to an element in the right panel
    const scrollToConditionGroup = useCallback((groupId) => {
        const element = conditionGroupRefs.current[groupId];
        if (element) {
            // Use scrollIntoView for precise positioning
            element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, []);
    // --- NEW: precise scroll to a specific condition ---
    const scrollToCondition = useCallback((conditionId) => {
        const element = conditionItemRefs.current[conditionId];
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, []);
    // Function to handle a click on a condition group in TriggerNode
    const handleConditionGroupClick = useCallback((groupId) => {
        setSelectedConditionGroupId(groupId);
        setSelectedConditionId(null); // Explicitly reset the specific condition selection
        requestAnimationFrame(() => {
            scrollToConditionGroup(groupId);
        });
    }, [scrollToConditionGroup]);
    // --- NEW: handle a click on a specific condition ---
    const handleConditionItemClick = useCallback((groupId, conditionId) => {
        // Just set both IDs; rAF will take care of scrolling after render
        setSelectedConditionGroupId(groupId);
        setSelectedConditionId(conditionId);
        requestAnimationFrame(() => {
            scrollToCondition(conditionId);
        });
    }, [scrollToCondition]);
    // Event listener to handle clicks on condition groups
    useEffect(() => {
        const handleConditionGroupClickEvent = (event) => {
            const customEvent = event;
            const { groupId } = customEvent.detail;
            handleConditionGroupClick(groupId);
        };
        window.addEventListener('conditionGroupClick', handleConditionGroupClickEvent);
        return () => {
            window.removeEventListener('conditionGroupClick', handleConditionGroupClickEvent);
        };
    }, [handleConditionGroupClick]);
    // Single useEffect to manage scrolling
    useLayoutEffect(() => {
        if (selectedConditionId) {
            // Priority #1: scroll to the specific selected condition
            scrollToCondition(selectedConditionId);
        }
        else if (selectedConditionGroupId) {
            // Priority #2: scroll to the group if no condition is selected
            scrollToConditionGroup(selectedConditionGroupId);
        }
    }, [selectedConditionGroupId, selectedConditionId, scrollToCondition, scrollToConditionGroup]);
    // --- NEW: listener for conditionItemClick ---
    useEffect(() => {
        const handleConditionItemClickEvent = (event) => {
            const customEvent = event;
            const { groupId, conditionId } = customEvent.detail;
            handleConditionItemClick(groupId, conditionId);
        };
        window.addEventListener('conditionItemClick', handleConditionItemClickEvent);
        return () => {
            window.removeEventListener('conditionItemClick', handleConditionItemClickEvent);
        };
    }, [handleConditionItemClick]);
    // Handler for updating a dialogue node
    const handleUpdateNode = useCallback((updatedData) => {
        if (selectedNode && selectedNode.type === 'event') {
            // --- NEW: Sync changes immediately with the events array ---
            if (updatedData.eventData) {
                setEvents(draft => {
                    const idx = draft.findIndex(ev => ev.id === updatedData.eventData.id);
                    if (idx >= 0) {
                        draft[idx] = Object.assign({}, updatedData.eventData);
                    }
                    else {
                        draft.push(Object.assign({}, updatedData.eventData));
                    }
                });
            }
        }
        if (!selectedNode)
            return;
        // Update the node in ReactFlow (always, for all nodes)
        updateNodes(draft => {
            const node = draft.find(n => n.id === selectedNode.id);
            if (node) {
                // Preserve the current position and other node properties
                const currentPosition = node.position;
                const currentSelected = node.selected;
                const currentDragging = node.dragging;
                // Update only the data, without affecting the position
                node.data = Object.assign(Object.assign(Object.assign({}, node.data), updatedData), { _forceUpdate: Date.now() // force ReactFlow to re-render the node
                 });
                // Restore position and state
                node.position = currentPosition;
                node.selected = currentSelected;
                node.dragging = currentDragging;
                // Update the style for the response node when the ability changes
                if (node.type === 'response' && updatedData.ability) {
                    // Explicitly set className to reflect the selected ability
                    if (updatedData.ability) {
                        node.className = `scenario-node response-node ${updatedData.ability.toLowerCase()}`;
                    }
                    else {
                        node.className = 'scenario-node response-node';
                    }
                }
            }
        });
        // Update selectedNode (always)
        setSelectedNode(prevNode => {
            if (prevNode) {
                return Object.assign(Object.assign({}, prevNode), updatedData);
            }
            return prevNode;
        });
        // Universally update the corresponding Dialogue object in the dialogues array
        updateDialogues(draft => {
            var _a, _b;
            const dlg = draft.find(d => d.id === selectedNode.dialogueId);
            if (!dlg)
                return;
            if (selectedNode.type === 'npc') {
                const dialogueNode = dlg.nodes.find(n => n.id === selectedNode.nodeId);
                if (dialogueNode) {
                    if (updatedData.text !== undefined)
                        dialogueNode.text = updatedData.text;
                    if (updatedData.npcImage !== undefined)
                        dialogueNode.npcImage = updatedData.npcImage;
                    if (updatedData.selectedNPCId !== undefined)
                        dialogueNode.selectedNPCId = updatedData.selectedNPCId;
                    if (updatedData.backgroundImage !== undefined)
                        dialogueNode.backgroundImage = updatedData.backgroundImage;
                    if (updatedData.options !== undefined) {
                        dialogueNode.responseOptions = ((_a = dialogueNode.responseOptions) === null || _a === void 0 ? void 0 : _a.map(ro => {
                            const upd = updatedData.options.find(o => o.id === ro.id);
                            return upd ? Object.assign(Object.assign({}, ro), { text: upd.text || ro.text, requiredCharacteristic: upd.ability, requiredCharacteristicValue: upd.requiredValue, rewardLevel: upd.rewardLevel || ro.rewardLevel }) : ro;
                        })) || dialogueNode.responseOptions;
                    }
                }
            }
            else if (selectedNode.type === 'response') {
                const dialogueNode = dlg.nodes.find(n => n.id === selectedNode.nodeId);
                if (dialogueNode) {
                    const responseOption = (_b = dialogueNode.responseOptions) === null || _b === void 0 ? void 0 : _b.find(o => `response-${o.id}` === selectedNode.id);
                    if (responseOption) {
                        if (updatedData.text)
                            responseOption.text = updatedData.text;
                        if (updatedData.ability !== undefined)
                            responseOption.requiredCharacteristic = updatedData.ability;
                        if (updatedData.requiredValue !== undefined)
                            responseOption.requiredCharacteristicValue = updatedData.requiredValue;
                        if (updatedData.rewardLevel)
                            responseOption.rewardLevel = updatedData.rewardLevel;
                    }
                }
            }
        });
        // Also update currentDialogue if it matches
        if (currentDialogue && selectedNode.dialogueId === currentDialogue.id) {
            updateCurrentDialogue(draft => {
                var _a, _b;
                // Logic identical to the above, but for currentDialogue
                if (!draft)
                    return;
                if (selectedNode.type === 'npc') {
                    const dialogueNode = draft.nodes.find(n => n.id === selectedNode.nodeId);
                    if (dialogueNode) {
                        if (updatedData.text !== undefined)
                            dialogueNode.text = updatedData.text;
                        if (updatedData.npcImage !== undefined)
                            dialogueNode.npcImage = updatedData.npcImage;
                        if (updatedData.selectedNPCId !== undefined)
                            dialogueNode.selectedNPCId = updatedData.selectedNPCId;
                        if (updatedData.backgroundImage !== undefined)
                            dialogueNode.backgroundImage = updatedData.backgroundImage;
                        if (updatedData.options !== undefined) {
                            dialogueNode.responseOptions = ((_a = dialogueNode.responseOptions) === null || _a === void 0 ? void 0 : _a.map(ro => {
                                const upd = updatedData.options.find(o => o.id === ro.id);
                                return upd ? Object.assign(Object.assign({}, ro), { text: upd.text || ro.text, requiredCharacteristic: upd.ability, requiredCharacteristicValue: upd.requiredValue, rewardLevel: upd.rewardLevel || ro.rewardLevel }) : ro;
                            })) || dialogueNode.responseOptions;
                        }
                    }
                }
                else if (selectedNode.type === 'response') {
                    const dialogueNode = draft.nodes.find(n => n.id === selectedNode.nodeId);
                    if (dialogueNode) {
                        const responseOption = (_b = dialogueNode.responseOptions) === null || _b === void 0 ? void 0 : _b.find(o => `response-${o.id}` === selectedNode.id);
                        if (responseOption) {
                            if (updatedData.text)
                                responseOption.text = updatedData.text;
                            if (updatedData.ability !== undefined)
                                responseOption.requiredCharacteristic = updatedData.ability;
                            if (updatedData.requiredValue !== undefined)
                                responseOption.requiredCharacteristicValue = updatedData.requiredValue;
                            if (updatedData.rewardLevel)
                                responseOption.rewardLevel = updatedData.rewardLevel;
                        }
                    }
                }
            });
        }
    }, [selectedNode, currentDialogue, updateNodes, updateCurrentDialogue, updateEdges, setEvents]);
    // Handler for adding a response option
    const handleAddResponseOption = useCallback(() => {
        if (!selectedNode || selectedNode.type !== 'npc')
            return;
        // Create a new response option
        const newOptionId = `response_${Date.now()}`;
        const newOption = {
            id: newOptionId,
            text: 'New response option',
            rewardLevel: DialogueRewardLevel.None
        };
        // Update the dialogue data model
        updateCurrentDialogue(draft => {
            if (draft) {
                const dialogueNode = draft.nodes.find(n => n.id === selectedNode.nodeId);
                if (dialogueNode) {
                    if (!dialogueNode.responseOptions) {
                        dialogueNode.responseOptions = [];
                    }
                    dialogueNode.responseOptions.push(newOption);
                }
            }
        });
        // Update the node in ReactFlow
        updateNodes(draft => {
            const node = draft.find(n => n.id === selectedNode.id);
            if (node && node.data.type === 'npc') {
                // Update options in the node data
                if (!node.data.options) {
                    node.data.options = [];
                }
                node.data.options.push({
                    id: newOptionId,
                    text: newOption.text,
                    ability: newOption.requiredCharacteristic,
                    requiredValue: newOption.requiredCharacteristicValue,
                    rewardLevel: newOption.rewardLevel
                });
            }
        });
        // Update selectedNode
        setSelectedNode(prevNode => {
            if (prevNode && prevNode.type === 'npc') {
                const newOptions = [...(prevNode.options || []), {
                        id: newOptionId,
                        text: newOption.text,
                        characteristic: newOption.requiredCharacteristic,
                        requiredValue: newOption.requiredCharacteristicValue,
                        rewardLevel: newOption.rewardLevel,
                        handlePosition: newOption.handlePosition
                    }];
                return Object.assign(Object.assign({}, prevNode), { options: newOptions });
            }
            return prevNode;
        });
        // Notify ReactFlow of the change to the node's internal structure
        updateNodeInternals(selectedNode.id);
        // Update the dialogues array (for correct rendering in DialogueEditor)
        updateDialogues(draft => {
            const dlg = draft.find(d => d.id === selectedNode.dialogueId);
            if (!dlg)
                return;
            const dlgNode = dlg.nodes.find(n => n.id === selectedNode.nodeId);
            if (dlgNode) {
                if (!dlgNode.responseOptions)
                    dlgNode.responseOptions = [];
                dlgNode.responseOptions.push(newOption);
            }
        });
    }, [selectedNode, currentDialogue, updateCurrentDialogue, updateNodes, updateNodeInternals]);
    // Handler for deleting a response option
    const handleDeleteResponseOption = useCallback((optionId) => {
        if (!selectedNode || selectedNode.type !== 'npc')
            return;
        // If the node is linked to a dialogue, update the dialogue data model
        if (currentDialogue && selectedNode.dialogueId === currentDialogue.id) {
            updateCurrentDialogue(draft => {
                if (draft) {
                    const dialogueNode = draft.nodes.find(n => n.id === selectedNode.nodeId);
                    if (dialogueNode && dialogueNode.responseOptions) {
                        dialogueNode.responseOptions = dialogueNode.responseOptions.filter(o => o.id !== optionId);
                    }
                }
            });
        }
        // Update the node in ReactFlow (for all nodes - both dialogue-linked and standalone)
        updateNodes(draft => {
            const node = draft.find(n => n.id === selectedNode.id);
            if (node && node.data.type === 'npc' && node.data.options) {
                // Remove the option from the node data
                node.data.options = node.data.options.filter(o => o.id !== optionId);
            }
        });
        // Update selectedNode
        setSelectedNode(prevNode => {
            if (prevNode && prevNode.type === 'npc' && prevNode.options) {
                const updatedOptions = prevNode.options.filter(o => o.id !== optionId);
                return Object.assign(Object.assign({}, prevNode), { options: updatedOptions, _forceUpdate: Date.now() });
            }
            return prevNode;
        });
        // Notify ReactFlow of the change to the node's internal structure
        updateNodeInternals(selectedNode.id);
        // Remove edges originating from the deleted option
        updateEdges(draft => draft.filter(e => !(e.source === selectedNode.id && e.sourceHandle === `response-${optionId}`)));
        // Force-update NPC nodes to re-render active handles
        updateNodes(draft => {
            draft.forEach(node => {
                if (node.type === 'npc') {
                    node.data = Object.assign(Object.assign({}, node.data), { _forceUpdate: Date.now() });
                }
            });
        });
        // Also update the dialogues array (for correct rendering in DialogueManager)
        updateDialogues(draft => {
            const dlg = draft.find(d => d.id === selectedNode.dialogueId);
            if (!dlg)
                return;
            const dlgNode = dlg.nodes.find(n => n.id === selectedNode.nodeId);
            if (dlgNode && dlgNode.responseOptions) {
                dlgNode.responseOptions = dlgNode.responseOptions.filter(o => o.id !== optionId);
            }
        });
    }, [selectedNode, currentDialogue, updateCurrentDialogue, updateNodes, updateEdges, updateNodeInternals]);
    // Handler for updating dialogue properties
    const handleUpdateDialogueProps = useCallback((field, value) => {
        updateCurrentDialogue(draft => {
            if (draft) {
                draft[field] = value;
            }
        });
    }, [updateCurrentDialogue]);
    // Function to toggle the visibility of the properties panel
    const togglePropertiesPanel = useCallback(() => {
        setIsPropertiesPanelVisible(prev => !prev);
    }, []);
    // Handler for adding an event to the canvas
    const handleAddEventNode = useCallback((event, position) => {
        // Create a new dialogue node
        const newNodeId = `event_${Date.now()}`;
        // Find an approximate position for the new node
        const defaultPosition = { x: 250, y: 250 };
        const nodePosition = position || defaultPosition;
        // Create a new ReactFlow node
        const newFlowNode = {
            id: newNodeId,
            type: 'event',
            position: nodePosition,
            data: {
                id: newNodeId,
                type: 'event',
                nodeId: newNodeId, // Use the same ID for nodeId
                dialogueId: (currentDialogue === null || currentDialogue === void 0 ? void 0 : currentDialogue.id) || 'standalone',
                text: event.title,
                eventId: event.id,
                eventImage: event.backgroundImage, // Use backgroundImage for the node background
                eventData: event // eventData still contains cardImage for the avatar inside the node
            }
        };
        // Add the node to the ReactFlow nodes list
        updateNodes(draft => {
            draft.push(newFlowNode);
        });
    }, [currentDialogue, updateNodes]);
    // Handler for adding a dialogue to the canvas - creates independent nodes
    const handleAddDialogueNodesFromDialogue = useCallback((dialogue, startPosition) => {
        // 0. Previously we ignored re-importing a dialogue if at least one of its
        //    nodes was already present on the canvas. As a result, structural changes
        //    (new NPC phrases or responses) were not displayed. Now we remove the
        //    previous "version" of the dialogue from the canvas and rebuild it from
        //    the current data.
        //
        // 0.1 Find all existing nodes of this dialogue
        const existingNodesForDialogue = nodes.filter(n => n.data.dialogueId === dialogue.id);
        // Map of existing dialogue nodes: nodeId -> FlowNode
        const existingMap = new Map();
        existingNodesForDialogue.forEach(n => existingMap.set(n.data.nodeId, n));
        const existingUpdates = new Map();
        // We do NOT immediately remove existing nodes — first mark which of them still exist
        // in the dialogue structure, and remove the "extra" ones later.
        const hasExistingNodes = existingNodesForDialogue.length > 0;
        // Properly handle startPosition for all cases
        let positionOffset = { x: 0, y: 0 };
        let basePosition = { x: 250, y: 50 };
        if (startPosition) {
            if (hasExistingNodes) {
                // If there are existing nodes, compute the offset to move the whole group
                const existingPositions = existingNodesForDialogue.map(n => n.position);
                const minX = Math.min(...existingPositions.map(p => p.x));
                const minY = Math.min(...existingPositions.map(p => p.y));
                positionOffset = {
                    x: startPosition.x - minX,
                    y: startPosition.y - minY
                };
                basePosition = { x: minX, y: minY }; // Preserve the base position
            }
            else {
                // For new dialogues, use startPosition as the base position
                basePosition = startPosition;
            }
        }
        const newFlowNodes = [];
        const newFlowEdges = [];
        const addedNodeIds = new Set();
        // To exclude duplicate edges (source|handle|target)
        const existingEdgeKeySet = new Set(edges.map(e => `${e.source}|${e.sourceHandle}`));
        const newEdgeKeySet = new Set();
        // Function to select the optimal target handle based on node positions
        const getOptimalTargetHandle = (sourcePos, targetPos) => {
            // Always use the side inputs.
            const deltaX = targetPos.x - sourcePos.x;
            return deltaX >= 0 ? 'target-left' : 'target-right';
        };
        // Simple logic for laying out the dialogue tree by its links
        // ------------------------------------------------------------------
        // Layout constants and helper for grid positioning of dialogue nodes
        // ------------------------------------------------------------------
        const nodePositions = new Map();
        // Recursive function to place a node and its descendants
        const placeNodeAndChildren = (dialogueNode, x, level = 0) => {
            var _a;
            // Already placed?
            if (nodePositions.has(dialogueNode.id))
                return;
            // Y depends only on the level; X is passed recursively
            const y = basePosition.y + level * (NODE_HEIGHT + V_GAP);
            // Save the position
            nodePositions.set(dialogueNode.id, { x, y });
            // Create the ReactFlow node
            const npcNodeId = `dlg_${dialogue.id}_${dialogueNode.id}`;
            const maybeExisting = existingMap.get(dialogueNode.id);
            const nodeData = {
                id: npcNodeId,
                type: 'npc',
                nodeId: dialogueNode.id,
                dialogueId: dialogue.id,
                text: dialogueNode.text,
                npcImage: dialogueNode.npcImage,
                selectedNPCId: dialogueNode.selectedNPCId,
                backgroundImage: getDialogueBackground(dialogue) || dialogueNode.backgroundImage,
                isRoot: dialogueNode.id === dialogue.rootNodeId,
                options: ((_a = dialogueNode.responseOptions) === null || _a === void 0 ? void 0 : _a.map(opt => ({
                    id: opt.id,
                    text: opt.text,
                    ability: opt.requiredCharacteristic,
                    requiredValue: opt.requiredCharacteristicValue,
                    rewardLevel: opt.rewardLevel,
                    connectionType: opt.nextDialogueId ? 'nested' : (opt.linkedToDialogueId ? 'link' : 'none')
                }))) || []
            };
            if (maybeExisting) {
                // Save the updated data to apply later
                existingUpdates.set(dialogueNode.id, nodeData);
            }
            else {
                // Add a new node
                newFlowNodes.push({
                    id: npcNodeId,
                    type: 'npc',
                    position: { x, y },
                    data: nodeData
                });
            }
            addedNodeIds.add(dialogueNode.id);
            // Collect nested child nodes
            const childSet = new Set();
            const childNodes = [];
            (dialogueNode.responseOptions || []).forEach(opt => {
                if (opt.nextDialogueId && !childSet.has(opt.nextDialogueId)) {
                    childSet.add(opt.nextDialogueId);
                    const child = dialogue.nodes.find(n => n.id === opt.nextDialogueId);
                    if (child)
                        childNodes.push(child);
                }
            });
            if (childNodes.length > 0) {
                const childX = x + NODE_WIDTH + H_GAP; // shift right relative to the parent
                childNodes.forEach((child, idx) => {
                    const childLevel = idx === 0 ? level : level + idx; // first child in the same row, the rest below
                    placeNodeAndChildren(child, childX, childLevel);
                });
            }
        };
        // Use the shared function to determine the dialogue's root node
        const rootNodeId = determineRootNodeId(dialogue);
        const rootNode = dialogue.nodes.find(node => node.id === rootNodeId);
        if (rootNode) {
            placeNodeAndChildren(rootNode, basePosition.x, 0);
        }
        // Place "orphan" nodes (not reached via nextDialogueId chains)
        dialogue.nodes.forEach(node => {
            if (!addedNodeIds.has(node.id)) {
                // Compute their level as 0 (a new row after the ones already used)
                // for simplicity – can be made more complex if needed.
                placeNodeAndChildren(node, basePosition.x, 0);
            }
        });
        // ------------------------------------------------------------
        // Create/update edges for ALL dialogue nodes (including ones already
        // present on the canvas)
        const getNodeByDlgId = (dlgNodeId) => {
            const existing = existingMap.get(dlgNodeId);
            if (existing) {
                // For existing nodes, apply the position offset
                return Object.assign(Object.assign({}, existing), { position: {
                        x: existing.position.x + positionOffset.x,
                        y: existing.position.y + positionOffset.y
                    } });
            }
            return newFlowNodes.find(n => n.data.nodeId === dlgNodeId);
        };
        dialogue.nodes.forEach(dlgNode => {
            const src = getNodeByDlgId(dlgNode.id);
            if (!src)
                return;
            (dlgNode.responseOptions || []).forEach(opt => {
                const isLink = opt.linkedToDialogueId !== undefined && opt.linkedToDialogueId !== null;
                const connectionType = isLink ? 'link' : (opt.nextDialogueId ? 'nested' : null);
                const targetId = isLink ? opt.linkedToDialogueId : opt.nextDialogueId;
                if (!targetId || !connectionType)
                    return;
                const tgt = getNodeByDlgId(targetId);
                if (!tgt)
                    return;
                const targetHandle = getOptimalTargetHandle(src.position, tgt.position);
                const key = `${src.id}|response-${opt.id}`;
                if (!existingEdgeKeySet.has(key) && !newEdgeKeySet.has(key)) {
                    newFlowEdges.push({
                        id: `edge-response-${opt.id}-${tgt.id}`,
                        source: src.id,
                        sourceHandle: `response-${opt.id}`,
                        target: tgt.id,
                        targetHandle: targetHandle,
                        type: 'buttonedge',
                        data: { connectionType }
                    });
                    newEdgeKeySet.add(key);
                }
            });
        });
        // Create/update the dialogue group BEFORE syncing the nodes
        const groupNodeId = `group_${dialogue.id}`;
        const existingGroupNode = nodes.find(n => n.id === groupNodeId && n.type === 'dialogue-group');
        // Count the number of dialogue nodes (including new ones)
        const dialogueNodesCount = newFlowNodes.length + existingUpdates.size;
        let groupNode = null;
        // Function to compute the group dimensions based on all dialogue nodes
        const calculateGroupBounds = () => {
            // Collect all dialogue node positions
            const allDialoguePositions = [];
            // First, add all existing dialogue nodes from the canvas (accounting for the offset)
            nodes.forEach(node => {
                if (node.data.dialogueId === dialogue.id && node.type !== 'dialogue-group') {
                    allDialoguePositions.push({
                        x: node.position.x + positionOffset.x,
                        y: node.position.y + positionOffset.y,
                        width: NODE_WIDTH,
                        height: NODE_HEIGHT
                    });
                }
            });
            // Add new nodes with their calculated positions
            newFlowNodes.forEach(node => {
                allDialoguePositions.push({
                    x: node.position.x,
                    y: node.position.y,
                    width: NODE_WIDTH,
                    height: NODE_HEIGHT
                });
            });
            // If there are no new or existing nodes, use basePosition
            if (allDialoguePositions.length === 0) {
                allDialoguePositions.push({
                    x: basePosition.x,
                    y: basePosition.y,
                    width: NODE_WIDTH,
                    height: NODE_HEIGHT
                });
            }
            // Compute the group bounds accounting for node dimensions
            const minX = Math.min(...allDialoguePositions.map(p => p.x));
            const minY = Math.min(...allDialoguePositions.map(p => p.y));
            const maxX = Math.max(...allDialoguePositions.map(p => p.x + p.width));
            const maxY = Math.max(...allDialoguePositions.map(p => p.y + p.height));
            // Add padding around the group, 15% larger than the content size
            const contentWidth = maxX - minX;
            const contentHeight = maxY - minY;
            const paddingX = Math.max(30, contentWidth * 0.15); // at least 30px or 15% of the width
            const paddingY = Math.max(30, contentHeight * 0.15); // at least 30px or 15% of the height
            const groupX = minX - paddingX;
            const groupY = minY - paddingY;
            const groupWidth = contentWidth + (paddingX * 2);
            const groupHeight = contentHeight + (paddingY * 2);
            return { x: groupX, y: groupY, width: groupWidth, height: groupHeight };
        };
        // Function to update the group dimensions after all nodes are placed
        const updateGroupBounds = () => {
            setTimeout(() => {
                // Use preservePosition = true to minimize "jumps"
                updateGroupBoundsForDialogue(dialogue.id, undefined, undefined, true);
            }, 50); // A small delay so the nodes have time to be placed
        };
        // If the group does not exist, create it
        if (!existingGroupNode && (newFlowNodes.length > 0 || existingUpdates.size > 0)) {
            const groupBounds = calculateGroupBounds();
            const groupNodeData = {
                id: groupNodeId,
                type: 'dialogue-group',
                nodeId: groupNodeId,
                dialogueId: dialogue.id,
                text: `Dialogue Group: ${dialogue.name}`,
                groupDialogueName: dialogue.name,
                groupChildrenCount: dialogueNodesCount
            };
            groupNode = {
                id: groupNodeId,
                type: 'dialogue-group',
                position: { x: groupBounds.x, y: groupBounds.y },
                data: groupNodeData,
                style: {
                    width: groupBounds.width,
                    height: groupBounds.height,
                    background: 'rgba(99, 102, 241, 0.05)',
                    border: '2px dashed rgba(99, 102, 241, 0.4)',
                    borderRadius: '12px',
                    zIndex: -10,
                    pointerEvents: 'auto' // Allow interaction with the group
                }
            };
        }
        // Sync the nodes:
        updateNodes(draft => {
            // 1. Add the group if created (the group must be FIRST)
            if (groupNode) {
                draft.unshift(groupNode);
            }
            // 2. Update the data of existing nodes and bind them to the group
            for (let i = 0; i < draft.length; i++) {
                const n = draft[i];
                if (n.data.dialogueId === dialogue.id && n.type !== 'dialogue-group') {
                    const upd = existingUpdates.get(n.data.nodeId);
                    // Apply the position offset if a starting position was provided
                    const newPosition = {
                        x: n.position.x + positionOffset.x,
                        y: n.position.y + positionOffset.y
                    };
                    if (upd) {
                        // Create a new object instead of mutating the existing one
                        draft[i] = Object.assign(Object.assign({}, n), { data: upd, parentId: groupNodeId, extent: undefined, position: {
                                x: newPosition.x - ((groupNode === null || groupNode === void 0 ? void 0 : groupNode.position.x) || 0),
                                y: newPosition.y - ((groupNode === null || groupNode === void 0 ? void 0 : groupNode.position.y) || 0)
                            } });
                    }
                    else {
                        // Bind the existing node to the group with the new position
                        draft[i] = Object.assign(Object.assign({}, n), { parentId: groupNodeId, extent: undefined, position: {
                                x: newPosition.x - ((groupNode === null || groupNode === void 0 ? void 0 : groupNode.position.x) || 0),
                                y: newPosition.y - ((groupNode === null || groupNode === void 0 ? void 0 : groupNode.position.y) || 0)
                            } });
                    }
                }
            }
            // 3. Add new nodes bound to the group
            const nodesWithParent = newFlowNodes.map(node => (Object.assign(Object.assign({}, node), { parentId: groupNodeId, extent: undefined, position: {
                    x: node.position.x - ((groupNode === null || groupNode === void 0 ? void 0 : groupNode.position.x) || 0),
                    y: node.position.y - ((groupNode === null || groupNode === void 0 ? void 0 : groupNode.position.y) || 0)
                } })));
            draft.push(...nodesWithParent);
            // 4. Remove stale nodes (those no longer present in the dialogue)
            const stillValidNodeIds = new Set(dialogue.nodes.map(n => `dlg_${dialogue.id}_${n.id}`));
            for (let i = draft.length - 1; i >= 0; i--) {
                const n = draft[i];
                if (n.data.dialogueId === dialogue.id && n.type !== 'dialogue-group' && !stillValidNodeIds.has(n.id)) {
                    draft.splice(i, 1);
                }
            }
        });
        // If the group exists, update the node count and dimensions
        if (existingGroupNode) {
            // Use preservePosition = true to minimize "jumps" when updating existing groups
            setTimeout(() => {
                updateGroupBoundsForDialogue(dialogue.id, undefined, undefined, true);
            }, 10);
        }
        // Defer invalidating the internal handles so the nodes are already mounted
        setTimeout(() => {
            [...existingUpdates.keys(), ...newFlowNodes.map(n => n.data.nodeId)].forEach(dlgNodeId => {
                const flowId = `dlg_${dialogue.id}_${dlgNodeId}`;
                updateNodeInternals(flowId);
            });
            // Force React Flow to recompute the edges after the new handles appear
            updateEdges(draft => {
                if (draft.length > 0) {
                    // A light "touch" – reorder the elements back and forth
                    const last = draft.pop();
                    draft.push(last);
                }
            });
            // Update the connection types so badges and styling apply immediately
            recomputeEdgeTypes();
        }, 0);
        updateEdges(draft => {
            newFlowEdges.forEach(newE => {
                const idx = draft.findIndex(ed => ed.source === newE.source && ed.sourceHandle === newE.sourceHandle);
                if (idx >= 0) {
                    // If the target changed – update the existing edge
                    if (draft[idx].target !== newE.target || draft[idx].targetHandle !== newE.targetHandle) {
                        draft[idx] = Object.assign(Object.assign({}, draft[idx]), { target: newE.target, targetHandle: newE.targetHandle, data: newE.data });
                    }
                }
                else {
                    draft.push(newE);
                }
            });
        });
        // 6. Remove edges that lead to (or originate from) non-existent nodes – so no "dangling" lines remain
        // --- TEMPORARILY disable the automatic cleanup of "dangling" edges,
        // since it may remove edges right after new nodes are added.
        // Explicitly clone edges so React Flow re-renders the edge layer
        updateEdges(draft => [...draft]);
        // After syncing the edges, ensure they all have a correct connectionType
        recomputeEdgeTypes();
        // Update the group dimensions after all nodes are placed
        // But only if the group already existed - new groups already have correct dimensions
        if (existingGroupNode) {
            updateGroupBounds();
        }
    }, [updateNodes, updateEdges, determineRootNodeId /* from utils */, nodes, edges, recomputeEdgeTypes]);
    // Update onDrop for universal drag-and-drop
    const onDrop = (event) => {
        var _a;
        event.preventDefault();
        const data = event.dataTransfer.getData('application/reactflow');
        if (!data)
            return;
        const parsed = JSON.parse(data);
        const reactFlowBounds = (_a = document.querySelector('.react-flow')) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        if (!reactFlowBounds)
            return;
        if (!reactFlowInstance)
            return;
        // Convert the cursor coordinates (within the browser window) to graph coordinates, accounting for the current zoom and pan
        const position = reactFlowInstance.project({
            x: event.clientX - reactFlowBounds.left,
            y: event.clientY - reactFlowBounds.top,
        });
        if (parsed.type === 'event') {
            // For events, encounters and eventData must be passed
            handleAddEventNode(parsed.data, position);
        }
        else if (parsed.type === 'dialogue') {
            // For dialogues, create/recreate nodes via the shared function
            handleAddDialogueToCanvas(parsed.data, position);
        }
        else if (parsed.type === 'dialogue-npc') {
            handleAddDialogueNode('npc', position);
        }
        else if (parsed.type === 'player-response') {
            handleAddDialogueNode('player-response', position);
        }
        else if (parsed.type === 'ux-panel') {
            handleAddUXNode('ux-panel', position);
        }
        else if (parsed.type === 'ux-notification') {
            handleAddUXNode('ux-notification', position);
        }
        else if (parsed.type === 'item-reward') {
            handleAddItemNode('item-reward', position);
        }
        else if (parsed.type === 'item-requirement') {
            handleAddItemNode('item-requirement', position);
        }
        else if (parsed.type === 'item-spent') {
            handleAddItemNode('item-spent', position);
        }
        else if (parsed.type === 'trigger-conditional') {
            handleAddTriggerNode('conditional', position);
        }
    };
    // Add handlers for the different node types
    const handleAddDialogueNode = useCallback((nodeType, position) => __awaiter(this, void 0, void 0, function* () {
        const newNodeId = `${nodeType}_${Date.now()}`;
        const defaultPosition = { x: 250, y: 250 };
        const nodePosition = position || defaultPosition;
        // If adding an NPC node and no dialogue is selected, create a new dialogue automatically
        let targetDialogue = currentDialogue;
        if (nodeType === 'npc' && !currentDialogue) {
            try {
                // Create a new dialogue with default values
                const newDialogue = createDefaultDialogue();
                newDialogue.name = "Untitled Dialogue";
                // Make sure rootNodeId is correct
                newDialogue.rootNodeId = determineRootNodeId(newDialogue);
                // Save on the server
                const createdDialogue = yield createDialogue(levelId, newDialogue);
                // Update the dialogues list
                updateDialogues(draft => {
                    draft.push(createdDialogue);
                });
                // Select the new dialogue
                setSelectedDialogueId(createdDialogue.id);
                updateCurrentDialogue(createdDialogue);
                targetDialogue = createdDialogue;
            }
            catch (error) {
                console.error('Error creating dialogue:', error);
                // If the dialogue could not be created, work as standalone
                targetDialogue = null;
            }
        }
        const newFlowNode = {
            id: newNodeId,
            type: nodeType === 'player-response' ? 'player-response' : nodeType,
            position: nodePosition,
            data: {
                id: newNodeId,
                type: nodeType === 'player-response' ? 'player-response' : nodeType,
                nodeId: newNodeId,
                dialogueId: (targetDialogue === null || targetDialogue === void 0 ? void 0 : targetDialogue.id) || 'standalone',
                text: nodeType === 'npc' ? 'New NPC dialogue' : 'New player response',
                options: []
            }
        };
        updateNodes(draft => {
            draft.push(newFlowNode);
        });
    }), [currentDialogue, updateNodes, levelId, updateDialogues, updateCurrentDialogue]);
    const handleAddUXNode = useCallback((type, position) => {
        // Create a template for the panel or notification
        const nodePosition = position || {
            x: Math.random() * 300 + 50,
            y: Math.random() * 300 + 50
        };
        // ID for the new node
        const newId = `node_${Date.now()}`;
        // Data depending on the type
        let nodeData;
        if (type === 'ux-panel') {
            nodeData = {
                id: newId,
                type: 'ux-panel',
                nodeId: newId,
                dialogueId: (currentDialogue === null || currentDialogue === void 0 ? void 0 : currentDialogue.id) || 'standalone',
                text: 'User interface panel',
                panelType: 'inventory' // default type
            };
        }
        else {
            nodeData = {
                id: newId,
                type: 'ux-notification',
                nodeId: newId,
                dialogueId: (currentDialogue === null || currentDialogue === void 0 ? void 0 : currentDialogue.id) || 'standalone',
                text: 'Notification message',
                notificationType: 'info', // default type
                duration: 5 // default duration in seconds
            };
        }
        // Create a new node
        const newNode = {
            id: newId,
            type,
            position: nodePosition,
            data: nodeData
        };
        // Add the node to the list
        updateNodes(draft => {
            draft.push(newNode);
        });
        // Show the properties panel
        setIsPropertiesPanelVisible(true);
    }, [currentDialogue, updateNodes, setIsPropertiesPanelVisible]);
    // Function to delete an entire dialogue group
    const handleDeleteDialogueGroup = useCallback((dialogueId) => {
        // Find all dialogue nodes and the group
        const nodesToDelete = nodes.filter(n => n.data.dialogueId === dialogueId ||
            (n.type === 'dialogue-group' && n.id === `group_${dialogueId}`));
        const nodeIdsToDelete = new Set(nodesToDelete.map(n => n.id));
        // Remove the nodes
        updateNodes(draft => draft.filter(n => !nodeIdsToDelete.has(n.id)));
        // Remove the related edges
        updateEdges(draft => draft.filter(e => !nodeIdsToDelete.has(e.source) && !nodeIdsToDelete.has(e.target)));
        // Reset the selected node if it was deleted
        if (selectedNodeRef.current && nodeIdsToDelete.has(selectedNodeRef.current.id)) {
            setSelectedNode(null);
        }
        // After deleting the entire group, automatically switch the panel to dialogue-add mode
        setSelectedCategory('dialogues');
    }, [nodes, updateNodes, updateEdges, setSelectedNode, setSelectedCategory]);
    const handleAddItemNode = useCallback((type, position) => {
        // Create a template for the item
        const nodePosition = position || {
            x: Math.random() * 300 + 50,
            y: Math.random() * 300 + 50
        };
        // ID for the new node
        const newId = `node_${Date.now()}`;
        // Data for the node
        const nodeData = {
            id: newId,
            type: type,
            nodeId: newId,
            dialogueId: (currentDialogue === null || currentDialogue === void 0 ? void 0 : currentDialogue.id) || 'standalone',
            text: type === 'item-reward' ? 'Player receives items' : type === 'item-requirement' ? 'Player needs items' : 'Player spends items',
            items: [] // start with an empty items array
        };
        // Create a new node
        const newNode = {
            id: newId,
            type,
            position: nodePosition,
            data: nodeData
        };
        // Add the node to the list
        updateNodes(draft => {
            draft.push(newNode);
        });
        // Show the properties panel
        setIsPropertiesPanelVisible(true);
    }, [currentDialogue, updateNodes, setIsPropertiesPanelVisible]);
    // Handlers for ConditionEditor
    const handleUpdateCondition = useCallback((groupIndex, conditionIndex, updates) => {
        if (!selectedNode)
            return;
        const groups = selectedNode.triggerConditionGroups || [];
        const newGroups = [...groups];
        newGroups[groupIndex] = Object.assign(Object.assign({}, newGroups[groupIndex]), { conditions: newGroups[groupIndex].conditions.map((cond, i) => i === conditionIndex ? Object.assign(Object.assign({}, cond), updates) : cond) });
        handleUpdateNode({ triggerConditionGroups: newGroups });
    }, [selectedNode, handleUpdateNode]);
    const handleRemoveCondition = useCallback((groupIndex, conditionIndex) => {
        if (!selectedNode)
            return;
        const groups = selectedNode.triggerConditionGroups || [];
        const newGroups = [...groups];
        newGroups[groupIndex] = Object.assign(Object.assign({}, newGroups[groupIndex]), { conditions: newGroups[groupIndex].conditions.filter((_, i) => i !== conditionIndex) });
        handleUpdateNode({ triggerConditionGroups: newGroups });
    }, [selectedNode, handleUpdateNode]);
    const handleAddTriggerNode = useCallback((triggerType, position) => {
        // Create a template for the trigger
        const nodePosition = position || {
            x: Math.random() * 300 + 50,
            y: Math.random() * 300 + 50
        };
        // ID for the new node
        const newId = `node_${Date.now()}`;
        // Get the appropriate text depending on the trigger type
        let triggerText = '';
        switch (triggerType) {
            case 'conditional':
                triggerText = 'Triggers when conditions are met';
                break;
        }
        // Data for the node
        const nodeData = {
            id: newId,
            type: 'trigger',
            nodeId: newId,
            dialogueId: (currentDialogue === null || currentDialogue === void 0 ? void 0 : currentDialogue.id) || 'standalone',
            text: triggerText,
            triggerType: triggerType,
            triggerCondition: undefined,
            triggerConditionGroups: [],
            // Add default values for the conditional trigger to avoid duplicate display
            triggerGroupType: 'MapPoint',
            triggerMapPoint: {
                nodeCode: '',
                visitType: 'OnVisit'
            }
        };
        // Create a new node
        const newNode = {
            id: newId,
            type: 'trigger',
            position: nodePosition,
            data: nodeData
        };
        // Add the node to the list
        updateNodes(draft => {
            draft.push(newNode);
        });
        // Show the properties panel
        setIsPropertiesPanelVisible(true);
    }, [currentDialogue, updateNodes, setIsPropertiesPanelVisible]);
    // Update the category selection handler
    const handleCategorySelect = useCallback((category) => {
        setSelectedCategory(prev => prev === category ? null : category);
        setSelectedNode(null);
    }, []);
    // Update the ReactFlow component, adding handlers to support drag-and-drop
    const onDragOver = (event) => {
        event.preventDefault();
    };
    /**
     * Function to get the category icon
     */
    const getCategoryIcon = (category) => {
        switch (category) {
            case 'dialogues':
                return _jsx(FaComments, {});
            case 'events':
                return _jsx(FaBolt, {});
            case 'ux':
                return _jsx(FaDesktop, {});
            case 'items':
                return _jsx(FaBox, {});
            case 'triggers':
                return _jsx(FaBullseye, {});
            default:
                return null;
        }
    };
    /**
     * Function to get the category title with proper localization
     */
    const getCategoryTitle = (category) => {
        switch (category) {
            case 'dialogues':
                return 'Dialogues';
            case 'events':
                return 'Events';
            case 'ux':
                return 'UX Elements';
            case 'items':
                return 'Items';
            case 'triggers':
                return 'Triggers';
            default:
                return '';
        }
    };
    /**
     * Function to get the node type icon
     */
    const getNodeTypeIcon = (nodeType) => {
        switch (nodeType) {
            case 'npc':
                return _jsx(FaUser, {});
            case 'response':
                return _jsx(FaComment, {});
            case 'player-response':
                return _jsx(FaUser, {});
            case 'event':
                return _jsx(FaBolt, {});
            case 'ux-panel':
                return _jsx(FaDesktop, {});
            case 'ux-notification':
                return _jsx(FaBell, {});
            case 'item-reward':
            case 'item-requirement':
                return _jsx(FaBox, {});
            case 'item-spent':
                return _jsx(FaTrash, {});
            case 'trigger':
                return _jsx(FaBullseye, {});
            case 'dialogue-group':
                return _jsx(FaComments, {});
            default:
                return _jsx(FaCogs, {});
        }
    };
    /**
     * Function to get the node type title
     */
    const getNodeTypeTitle = (nodeType) => {
        switch (nodeType) {
            case 'npc':
                return 'NPC Dialogue';
            case 'response':
                return 'Player Response';
            case 'player-response':
                return 'Player Response';
            case 'event':
                return 'Event';
            case 'ux-panel':
                return 'UX Panel';
            case 'ux-notification':
                return 'Notification';
            case 'item-reward':
                return 'Item Reward';
            case 'item-requirement':
                return 'Item Requirement';
            case 'item-spent':
                return 'Item Spent';
            case 'trigger':
                return 'Trigger';
            case 'dialogue-group':
                return 'Dialogue Group';
            default:
                return 'Element';
        }
    };
    // Functions for filtering events
    const getFilteredEvents = () => {
        return events.filter(event => {
            // Filter by search query
            const matchesSearch = !eventsSearchTerm ||
                event.title.toLowerCase().includes(eventsSearchTerm.toLowerCase()) ||
                event.description.toLowerCase().includes(eventsSearchTerm.toLowerCase());
            // Filter by abilities
            const matchesCharacteristics = selectedEventCharacteristics.length === 0 ||
                event.encounters.some(encounter => selectedEventCharacteristics.includes(encounter.ability));
            return matchesSearch && matchesCharacteristics;
        });
    };
    // Handlers for filtering events
    const handleEventsSearchChange = (value) => {
        setEventsSearchTerm(value);
    };
    const handleEventCharacteristicToggle = (characteristic) => {
        setSelectedEventCharacteristics(prev => {
            if (prev.includes(characteristic)) {
                return prev.filter(c => c !== characteristic);
            }
            else {
                return [...prev, characteristic];
            }
        });
    };
    const handleClearEventsFilters = () => {
        setEventsSearchTerm('');
        setSelectedEventCharacteristics([]);
    };
    // Functions for filtering dialogues
    const getFilteredDialogues = () => {
        return dialogues.filter(dialogue => {
            if (!dialogue || dialogue.isArchived)
                return false;
            // Filter by search query
            const matchesSearch = !dialoguesSearchTerm ||
                dialogue.name.toLowerCase().includes(dialoguesSearchTerm.toLowerCase());
            // Filter by dialogue types (simple logic based on the name)
            const matchesTypes = selectedDialogueTypes.length === 0 ||
                selectedDialogueTypes.some(type => {
                    switch (type) {
                        case 'NPC': return dialogue.name.toLowerCase().includes('merchant') ||
                            dialogue.name.toLowerCase().includes('guard') ||
                            dialogue.name.toLowerCase().includes('npc');
                        case 'Interactive': return dialogue.name.toLowerCase().includes('interactive');
                        case 'Quest': return dialogue.name.toLowerCase().includes('quest');
                        case 'Combat': return dialogue.name.toLowerCase().includes('combat') ||
                            dialogue.name.toLowerCase().includes('battle');
                        default: return true;
                    }
                });
            return matchesSearch && matchesTypes;
        });
    };
    // Handlers for filtering dialogues
    const handleDialoguesSearchChange = (value) => {
        setDialoguesSearchTerm(value);
    };
    const handleDialogueTypeToggle = (type) => {
        setSelectedDialogueTypes(prev => {
            if (prev.includes(type)) {
                return prev.filter(t => t !== type);
            }
            else {
                return [...prev, type];
            }
        });
    };
    const handleClearDialoguesFilters = () => {
        setDialoguesSearchTerm('');
        setSelectedDialogueTypes([]);
    };
    // Function to add a dialogue to the canvas
    const handleAddDialogueToCanvas = (dialogue, position) => __awaiter(this, void 0, void 0, function* () {
        // Check whether a group for this dialogue is already on the canvas
        const groupNodeId = `group_${dialogue.id}`;
        const hasGroupOnCanvas = nodes.some(n => n.id === groupNodeId && n.type === 'dialogue-group');
        // If the group already exists and a position was provided (drag & drop), prevent re-adding
        if (hasGroupOnCanvas && position) {
            return;
        }
        // Check whether NPC nodes of this dialogue remain on the canvas
        const hasNpcNodesOnCanvas = nodes.some((n) => { var _a; return n.type === 'npc' && ((_a = n.data) === null || _a === void 0 ? void 0 : _a.dialogueId) === dialogue.id; });
        // If there are no nodes, the dialogue was deleted entirely – load a fresh version
        let sourceDialogue = dialogue;
        if (!hasNpcNodesOnCanvas) {
            try {
                sourceDialogue = yield getDialogueById(levelId, dialogue.id);
            }
            catch (err) {
                console.error('Failed to fetch fresh dialogue', err);
            }
        }
        // Create (or recreate) the dialogue nodes on the canvas
        handleAddDialogueNodesFromDialogue(sourceDialogue, position);
        // We no longer hide the right panel after adding a dialogue,
        // so it stays in the same state as when adding Events.
    });
    // --- Scenario saving logic (renamed) ---
    const saveScenario = () => __awaiter(this, void 0, void 0, function* () {
        if (!scenarioName.trim())
            return;
        setIsSavingScenario(true);
        setScenarioError(null);
        try {
            // 1. Save ALL dialogues present in the dialogues state
            for (const dialogue of dialogues) {
                try {
                    yield updateDialogue(levelId, dialogue.id, dialogue);
                }
                catch (dlgErr) {
                    console.error(`Error saving dialogue ${dialogue.id}:`, dlgErr);
                }
            }
            // 1b. Save all events that have nodes on the canvas
            const eventIdsInScenario = new Set(nodes
                .filter(n => { var _a; return n.type === 'event' && ((_a = n.data) === null || _a === void 0 ? void 0 : _a.eventId); })
                .map(n => n.data.eventId));
            for (const ev of events) {
                if (!eventIdsInScenario.has(ev.id))
                    continue;
                try {
                    yield updateEvent(levelId, ev.id, ev);
                }
                catch (evErr) {
                    console.error(`Error saving event ${ev.id}:`, evErr);
                }
            }
            // After all events are saved successfully, reset the changes flag
            setHasUnsavedEventChanges(false);
            // 2. Then build and save the scenario
            const id = scenarioId || `scenario_${uuidv4()}`;
            // --- 2. Build a copy of the nodes WITHOUT heavy eventData, to avoid caching stale events in the DB ---
            const sanitizedNodes = nodes.map(n => {
                if (n.type !== 'event')
                    return n;
                const cleanData = Object.assign({}, n.data);
                delete cleanData.eventData;
                delete cleanData._forceUpdate; // internal field
                return Object.assign(Object.assign({}, n), { data: cleanData });
            });
            const scenarioObj = {
                id,
                levelId,
                name: scenarioName.trim(),
                nodes: sanitizedNodes,
                edges
            };
            let savedScenario;
            if (scenarioId) {
                // Shared mutation service: owns the attempt → updated | failed protocol
                const saved = yield persistScenarioToServer(levelId, scenarioObj, ScenarioEditor.name);
                if (!saved)
                    throw new Error('Failed to save scenario');
                savedScenario = saved;
            }
            else {
                savedScenario = yield createScenario(levelId, scenarioObj);
                EventBus.emit('scenarioUpdated', savedScenario);
            }
            // Notify the parent component
            if (onScenarioSaved)
                onScenarioSaved(savedScenario);
            // Reset the scenario name
            setScenarioName('');
            // Go back if needed
            if (onBack)
                onBack();
        }
        catch (error) {
            console.error('Error saving scenario:', error);
            setScenarioError('Failed to save scenario');
        }
        finally {
            setIsSavingScenario(false);
        }
    });
    /* ------------------------------------------------------------------
       SYNC DIALOGUE DATA WITH FLOW NODES – must execute before conditional returns
    ------------------------------------------------------------------ */
    useEffect(() => {
        if (dialogues.length === 0 || nodes.length === 0)
            return;
        const validHandles = new Set();
        let nodesWereUpdated = false;
        updateNodes(nodeDrafts => {
            nodeDrafts.forEach(nodeDraft => {
                if (nodeDraft.type !== 'npc')
                    return;
                const dialogue = dialogues.find(x => x.id === nodeDraft.data.dialogueId);
                if (!dialogue)
                    return;
                const dialogueNode = dialogue.nodes.find(x => x.id === nodeDraft.data.nodeId);
                if (!dialogueNode)
                    return;
                const freshOptions = (dialogueNode.responseOptions || []).map(opt => ({
                    id: opt.id,
                    text: opt.text,
                    ability: opt.requiredCharacteristic,
                    requiredValue: opt.requiredCharacteristicValue,
                    rewardLevel: opt.rewardLevel,
                    connectionType: opt.nextDialogueId ? 'nested' : (opt.linkedToDialogueId ? 'link' : 'none')
                }));
                // Determine what background should be shown.
                let freshBackground;
                if (dialogue.backgroundMode === DialogueBackgroundMode.Map) {
                    // Map mode – rely on map-cell image if it exists; otherwise any explicit
                    // override saved inside the dialogue node.
                    freshBackground = dialogueNode.backgroundImage || nodeDraft.data.backgroundImage;
                }
                else {
                    // Custom mode – keep custom image first, then any node-level fallback.
                    freshBackground = getDialogueBackground(dialogue) || dialogueNode.backgroundImage || nodeDraft.data.backgroundImage;
                }
                const needUpdate = nodeDraft.data.text !== dialogueNode.text ||
                    !jsonDeepEqual(nodeDraft.data.options, freshOptions) ||
                    nodeDraft.data.selectedNPCId !== dialogueNode.selectedNPCId ||
                    nodeDraft.data.npcImage !== dialogueNode.npcImage ||
                    nodeDraft.data.backgroundImage !== freshBackground;
                if (needUpdate) {
                    nodesWereUpdated = true;
                    nodeDraft.data = Object.assign(Object.assign({}, nodeDraft.data), { text: dialogueNode.text, selectedNPCId: dialogueNode.selectedNPCId, npcImage: dialogueNode.npcImage, backgroundImage: freshBackground, options: freshOptions });
                }
                freshOptions.forEach(opt => validHandles.add(`response-${opt.id}`));
            });
        });
        if (nodesWereUpdated) {
            updateEdges(draft => draft.filter(e => { var _a; return !((_a = e.sourceHandle) === null || _a === void 0 ? void 0 : _a.startsWith('response-')) || validHandles.has(e.sourceHandle); }));
            nodes.filter(n => n.type === 'npc').forEach(n => updateNodeInternals(n.id));
        }
        // Additionally clean up edges that are no longer represented by either nextDialogueId or linkedToDialogueId
        if (dialogues.length > 0) {
            const allowedEdgeSet = new Set();
            dialogues.forEach(dlg => {
                dlg.nodes.forEach(n => {
                    (n.responseOptions || []).forEach(opt => {
                        const targetId = opt.nextDialogueId || opt.linkedToDialogueId;
                        if (targetId) {
                            const srcFlowId = `dlg_${dlg.id}_${n.id}`;
                            allowedEdgeSet.add(`${srcFlowId}|${opt.id}|${targetId}`);
                        }
                    });
                });
            });
            updateEdges(draft => draft.filter(e => {
                var _a;
                if (!((_a = e.sourceHandle) === null || _a === void 0 ? void 0 : _a.startsWith('response-')))
                    return true;
                // Keep external links (target is NOT an NPC) without checking allowedEdgeSet
                const tgtNode = nodes.find(n => n.id === e.target);
                if (!tgtNode || tgtNode.type !== 'npc')
                    return true;
                const optId = e.sourceHandle.replace('response-', '');
                return allowedEdgeSet.has(`${e.source}|${optId}|${(tgtNode.data.nodeId) || ''}`);
            }));
        }
    }, [dialogues, nodes, updateNodes, updateEdges, updateNodeInternals]);
    /* ------------------------------------------------------------------
       SMART REFRESH: rebuild the graph ONLY if the STRUCTURE changed
       (NPC nodes or responses added / removed). Changes to text,
       images, and other fields do not affect node positions.
    ------------------------------------------------------------------ */
    const prevDlgStructureRef = React.useRef({});
    const computeDlgHash = (dlg) => {
        // Hash = list of nodeId + for each node a list of responseId and nextDialogueId
        const parts = [];
        dlg.nodes.forEach(n => {
            const respIds = (n.responseOptions || [])
                .map(r => {
                const link = r.linkedToDialogueId;
                return `${r.id}->${r.nextDialogueId || link || ''}`;
            })
                .sort()
                .join('|');
            parts.push(`${n.id}[${respIds}]`);
        });
        return parts.sort().join('||');
    };
    useEffect(() => {
        if (nodes.length === 0)
            return;
        const dialoguesOnCanvas = new Set(nodes
            .filter((n) => { var _a; return n.type === 'npc' && ((_a = n.data) === null || _a === void 0 ? void 0 : _a.dialogueId) && n.data.dialogueId !== 'standalone'; })
            .map((n) => n.data.dialogueId));
        dialogues.forEach(dlg => {
            if (!dialoguesOnCanvas.has(dlg.id))
                return;
            const newHash = computeDlgHash(dlg);
            const prevHash = prevDlgStructureRef.current[dlg.id];
            if (newHash !== prevHash) {
                // the structure changed – rebuild
                handleAddDialogueNodesFromDialogue(dlg);
                prevDlgStructureRef.current[dlg.id] = newHash;
            }
        });
        // --- Capture the base snapshot if it has not been taken yet and the data is ready ---
        if (!initialSnapshotTakenRef.current && !isLoading && stateReadyRef.current) {
            initialSnapshotRef.current = buildSnapshot(nodes, edges, scenarioName.trim());
            initialSnapshotTakenRef.current = true;
            onUnsavedChangesChange === null || onUnsavedChangesChange === void 0 ? void 0 : onUnsavedChangesChange(false, scenarioName.trim());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dialogues, nodes]);
    // Recompute the connection types on the first dialogue load and after normalization
    useEffect(() => {
        if (nodes.length > 0 && dialogues.length > 0) {
            recomputeEdgeTypes();
        }
    }, [dialogues, nodes, recomputeEdgeTypes]);
    // When edges are loaded from the scenario, they may not yet have data.connectionType –
    // in this effect we compute it once so the badges display immediately.
    useEffect(() => {
        if (edges.length === 0)
            return;
        const needs = edges.some(e => { var _a; return !((_a = e.data) === null || _a === void 0 ? void 0 : _a.connectionType); });
        if (needs) {
            recomputeEdgeTypes();
        }
    }, [edges, recomputeEdgeTypes]);
    // RESTORE dialogues that were completely removed from the canvas
    useEffect(() => {
        // Collect the IDs of dialogues whose NPC nodes are still on the canvas
        const dialoguesOnCanvas = new Set(nodes
            .filter((n) => { var _a; return n.type === 'npc' && ((_a = n.data) === null || _a === void 0 ? void 0 : _a.dialogueId) && n.data.dialogueId !== 'standalone'; })
            .map((n) => n.data.dialogueId));
        // Determine dialogues that have no nodes left and whose structure has become empty
        const emptied = dialogues.filter((d) => d.nodes.length === 0 && !dialoguesOnCanvas.has(d.id));
        if (emptied.length === 0)
            return;
        emptied.forEach((dlg) => {
            (() => __awaiter(this, void 0, void 0, function* () {
                try {
                    const original = yield getDialogueById(levelId, dlg.id);
                    updateDialogues((draft) => {
                        const idx = draft.findIndex((d) => d.id === dlg.id);
                        if (idx >= 0)
                            draft[idx] = original;
                    });
                }
                catch (err) {
                    // If such a dialogue does not exist on the server yet (new), just ignore
                    console.warn('Could not restore dialogue', dlg.id, err);
                }
            }))();
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nodes]);
    // --- Subscription to global dialogue/event update events ---
    useEffect(() => {
        const offDlg = EventBus.on('dialogueUpdated', (dlg) => {
            updateDialogues(draft => {
                const idx = draft.findIndex(d => d.id === dlg.id);
                if (idx >= 0)
                    draft[idx] = dlg;
            });
        });
        const offEv = EventBus.on('eventUpdated', (ev) => {
            // 1. Update the events array
            setEvents(draft => {
                const idx = draft.findIndex(e => e.id === ev.id);
                if (idx >= 0)
                    draft[idx] = ev;
            });
            // 2. Update all Event nodes associated with this event
            const changedIds = [];
            updateNodes(nodesDraft => {
                nodesDraft.forEach(n => {
                    var _a;
                    if (n.type === 'event' && ((_a = n.data) === null || _a === void 0 ? void 0 : _a.eventId) === ev.id) {
                        n.data = Object.assign(Object.assign({}, n.data), { text: ev.title, eventImage: ev.backgroundImage, eventData: ev, _forceUpdate: Date.now() });
                        changedIds.push(n.id);
                    }
                });
            });
            // 3. Invalidate the internal ReactFlow cache for the affected nodes
            changedIds.forEach(id => updateNodeInternals(id));
            // 4. Tell Review that the scenario has handled the event change
            if (changedIds.length > 0 && scenarioId) {
                EventBus.emit('scenarioUpdated', { id: scenarioId });
            }
            // --- Do not treat this as a user-made scenario change ---
            if (initialSnapshotTakenRef.current && changedIds.length > 0) {
                // Take a new base snapshot so the "dirt" disappears
                setTimeout(() => {
                    initialSnapshotRef.current = buildSnapshot(nodes, edges, scenarioName.trim());
                    onUnsavedChangesChange === null || onUnsavedChangesChange === void 0 ? void 0 : onUnsavedChangesChange(false, scenarioName.trim());
                }, 0);
            }
        });
        const offEvDel = EventBus.on('eventDeleted', (evId) => {
            // 1. Remove from the events list
            setEvents(draft => draft.filter(e => e.id !== evId));
            // 2. Remove the related nodes and collect their ids
            const removedIds = [];
            updateNodes(nodesDraft => {
                var _a;
                for (let i = nodesDraft.length - 1; i >= 0; i--) {
                    const n = nodesDraft[i];
                    if (n.type === 'event' && ((_a = n.data) === null || _a === void 0 ? void 0 : _a.eventId) === evId) {
                        removedIds.push(n.id);
                        nodesDraft.splice(i, 1);
                    }
                }
            });
            // 3. Clean up edges that led to the removed nodes
            if (removedIds.length > 0) {
                const removedSet = new Set(removedIds);
                updateEdges(edgesDraft => edgesDraft.filter(e => !removedSet.has(e.source) && !removedSet.has(e.target)));
                recomputeEdgeTypes();
                removedSet.forEach(id => updateNodeInternals(id));
            }
        });
        const offSc = EventBus.on('scenarioUpdated', (sc) => {
            if (sc.id !== scenarioId)
                return;
            // Update nodes and edges so changes (e.g. the background) apply immediately
            if (sc.nodes) {
                updateNodes(() => sc.nodes);
            }
            if (sc.edges) {
                updateEdges(() => sc.edges);
            }
        });
        const offDlgDel = EventBus.on('dialogueDeleted', (dlgId) => {
            // 1. Remove the dialogue itself from the local state
            updateDialogues(draft => {
                const idx = draft.findIndex(d => d.id === dlgId);
                if (idx >= 0)
                    draft.splice(idx, 1);
            });
            // 2. Collect the ids of all nodes (any type) belonging to this dialogue, to remove them
            const removedIds = [];
            updateNodes(nodesDraft => {
                var _a;
                for (let i = nodesDraft.length - 1; i >= 0; i--) {
                    const n = nodesDraft[i];
                    if (((_a = n.data) === null || _a === void 0 ? void 0 : _a.dialogueId) === dlgId) {
                        removedIds.push(n.id);
                        nodesDraft.splice(i, 1);
                    }
                }
            });
            // 3. Clean up edges leading to the removed nodes
            if (removedIds.length > 0) {
                const removedSet = new Set(removedIds);
                updateEdges(edgesDraft => edgesDraft.filter(e => !removedSet.has(e.source) && !removedSet.has(e.target)));
                recomputeEdgeTypes();
                // Update the ReactFlow cache (positions / number of handles)
                removedSet.forEach(nid => updateNodeInternals(nid));
            }
        });
        return () => { offDlg(); offDlgDel(); offEv(); offEvDel(); offSc(); };
    }, [updateDialogues, setEvents, updateNodes, updateEdges, recomputeEdgeTypes, updateNodeInternals, scenarioId]);
    // --- Cleanup of "dangling" Event nodes whose events were deleted ---
    useEffect(() => {
        if (events.length === 0)
            return;
        const validEventIds = new Set(events.map(ev => ev.id));
        const removedIds = [];
        let anyNodeChanged = false;
        updateNodes(draft => {
            var _a;
            for (let i = draft.length - 1; i >= 0; i--) {
                const n = draft[i];
                if (n.type !== 'event')
                    continue;
                const evId = (_a = n.data) === null || _a === void 0 ? void 0 : _a.eventId;
                if (!evId)
                    continue;
                // Remove nodes without a valid event
                if (!validEventIds.has(evId)) {
                    removedIds.push(n.id);
                    draft.splice(i, 1);
                    anyNodeChanged = true;
                    continue;
                }
                // Data synchronization
                const ev = events.find(e => e.id === evId);
                if (!ev)
                    continue;
                const needsUpdate = !n.data.eventData || n.data.eventData.version !== ev.version;
                if (needsUpdate) {
                    n.data = Object.assign(Object.assign({}, n.data), { text: ev.title, eventImage: ev.backgroundImage, eventData: ev, _forceUpdate: Date.now() });
                    anyNodeChanged = true;
                }
            }
        });
        let scenarioTouched = false;
        if (removedIds.length > 0) {
            const removedSet = new Set(removedIds);
            updateEdges(edDraft => edDraft.filter(e => !removedSet.has(e.source) && !removedSet.has(e.target)));
            recomputeEdgeTypes();
            scenarioTouched = true;
        }
        // If there were deletions – recomputeEdgeTypes was already called; if only updates – call it manually
        if (!removedIds.length && anyNodeChanged) {
            recomputeEdgeTypes();
            scenarioTouched = true;
        }
        // Tell Review that the scenario has put itself in order
        if (scenarioTouched && scenarioId) {
            EventBus.emit('scenarioUpdated', { id: scenarioId });
        }
    }, [events, updateNodes, updateEdges, recomputeEdgeTypes]);
    /* ------------------------------------------------------------------
       Memoized JSX for unsaved-changes confirmation modal
    ------------------------------------------------------------------ */
    const unsavedModal = (_jsx(ConfirmationModal, { isOpen: isUnsavedModalOpen, onClose: () => {
            // Discard changes
            if (selectedNode && selectedNode.type === 'event' && originalEventSnapshot) {
                updateNodes(draft => {
                    const n = draft.find(nd => nd.id === selectedNode.id);
                    if (n) {
                        n.data.eventData = JSON.parse(JSON.stringify(originalEventSnapshot));
                        n.data.text = originalEventSnapshot.title;
                        n.data.eventImage = originalEventSnapshot.backgroundImage;
                    }
                });
                setHasUnsavedEventChanges(false);
            }
            setIsUnsavedModalOpen(false);
            proceedPendingAction();
        }, onConfirm: () => {
            // Save the event changes automatically on the next scenario save
            setHasUnsavedEventChanges(false);
            setIsUnsavedModalOpen(false);
            proceedPendingAction();
        }, title: "Unsaved changes", message: (_jsxs("div", { className: "warning-message", children: [_jsxs("div", { className: "warning-header", children: ["Unsaved changes for \"", (_a = selectedNode === null || selectedNode === void 0 ? void 0 : selectedNode.eventData) === null || _a === void 0 ? void 0 : _a.title, "\""] }), _jsx("div", { className: "warning-footer", children: "Do you want to save them?" })] })), confirmText: "Save", cancelText: "Discard", isLoading: false }));
    // --- Scenario save confirmation modal ---
    function handleSaveScenario() {
        if (!scenarioName.trim())
            return;
        // Build the list of dependencies affected by saving the scenario
        const dialogueIds = new Set();
        const eventIds = new Set();
        nodes.forEach(n => {
            if (n.type === 'npc') {
                if (n.data.dialogueId && n.data.dialogueId !== 'standalone') {
                    dialogueIds.add(n.data.dialogueId);
                }
            }
            else if (n.type === 'event') {
                if (n.data.eventId) {
                    eventIds.add(n.data.eventId);
                }
            }
        });
        // Get the dialogue names
        const dialogueNames = dialogues
            .filter(d => dialogueIds.has(d.id))
            .map(d => d.name);
        // Get the event names
        const eventObjs = events.filter(ev => eventIds.has(ev.id));
        const eventNames = eventObjs.map(ev => ev.title);
        // Scenarios in which these events appear (besides the current one)
        const impactedScenarioNames = scenarios
            .filter(sc => { var _a; return sc.id !== scenarioId && ((_a = sc.nodes) === null || _a === void 0 ? void 0 : _a.some((n) => { var _a; return eventIds.has((_a = n === null || n === void 0 ? void 0 : n.data) === null || _a === void 0 ? void 0 : _a.eventId); })); })
            .map(sc => sc.name);
        // Map nodes
        const directEncounterCoords = [];
        const allEncounterCoords = [];
        Object.entries(levelNodes).forEach(([coord, node]) => {
            var _a;
            const re = node.randomEncounters;
            if (!re || !re.enabled)
                return;
            if (re.encounterSelection === 'custom') {
                if ((_a = re.selectedEncounters) === null || _a === void 0 ? void 0 : _a.some((eid) => eventIds.has(eid))) {
                    directEncounterCoords.push(coord);
                }
            }
            else if (re.encounterSelection === 'all') {
                if (eventObjs.some(ev => ev.isEncounter)) {
                    allEncounterCoords.push(coord);
                }
            }
        });
        const hasLinks = dialogueNames.length > 0 || eventNames.length > 0 || impactedScenarioNames.length > 0 ||
            directEncounterCoords.length > 0 || allEncounterCoords.length > 0;
        const msg = (_jsxs("div", { className: "warning-message", children: [_jsx("div", { className: "warning-header", children: hasLinks ? 'Saving this scenario will update the following entities:' : 'Save scenario?' }), hasLinks && (_jsxs("div", { className: "connections-list", children: [dialogueNames.length > 0 && (_jsxs("div", { className: "connection-group scenarios", children: [_jsxs("div", { className: "connection-header", children: [_jsx(FaComments, { className: "connection-icon" }), " ", _jsx("span", { children: "Dialogues" })] }), _jsx("div", { className: "connection-items", children: dialogueNames.map((name, idx) => (_jsx("span", { className: "connection-item", children: name }, idx))) })] })), eventNames.length > 0 && (_jsxs("div", { className: "connection-group events", children: [_jsxs("div", { className: "connection-header", children: [_jsx(FaBolt, { className: "connection-icon" }), " ", _jsx("span", { children: "Events" })] }), _jsx("div", { className: "connection-items", children: eventNames.map((title, idx) => (_jsx("span", { className: "connection-item", children: title }, idx))) })] })), impactedScenarioNames.length > 0 && (_jsxs("div", { className: "connection-group scenarios", children: [_jsxs("div", { className: "connection-header", children: [_jsx(FaLink, { className: "connection-icon" }), " ", _jsx("span", { children: "Scenarios" })] }), _jsx("div", { className: "connection-items", children: impactedScenarioNames.map((name, idx) => (_jsx("span", { className: "connection-item", children: name }, idx))) })] })), (directEncounterCoords.length > 0 || allEncounterCoords.length > 0) && (_jsxs("div", { className: "connection-group map-nodes", children: [_jsxs("div", { className: "connection-header", children: [_jsx(FaMap, { className: "connection-icon" }), " ", _jsx("span", { children: "Map nodes" })] }), _jsx("div", { className: "connection-items", children: [...directEncounterCoords, ...allEncounterCoords].map((coord, idx) => (_jsx("span", { className: "connection-item", children: coord }, idx))) })] }))] })), _jsx("div", { className: "warning-footer", children: "Do you want to proceed and save changes?" })] }));
        setScenarioSaveMessage(msg);
        setIsScenarioSaveModalOpen(true);
    }
    /* ------------------------------------------------------------------
       UNSAVED CHANGES TRACKING – must be BEFORE the early returns
    ------------------------------------------------------------------ */
    const initialSnapshotRef = useRef('');
    const initialSnapshotTakenRef = useRef(false);
    const stateReadyRef = useRef(false);
    // Reset state when a new scenario starts loading
    useEffect(() => {
        if (isLoading) {
            initialSnapshotTakenRef.current = false;
            initialSnapshotRef.current = '';
            stateReadyRef.current = false;
        }
    }, [isLoading]);
    // Take a snapshot once the editor reports that the data is ready
    useEffect(() => {
        if (!stateReadyRef.current)
            return;
        if (initialSnapshotTakenRef.current)
            return;
        initialSnapshotRef.current = buildSnapshot(nodes, edges, scenarioName.trim());
        initialSnapshotTakenRef.current = true;
        onUnsavedChangesChange === null || onUnsavedChangesChange === void 0 ? void 0 : onUnsavedChangesChange(false, scenarioName.trim());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nodes, edges, scenarioName]);
    const hasUnsavedScenarioChanges = useMemo(() => {
        if (!initialSnapshotTakenRef.current) {
            return false;
        }
        const current = buildSnapshot(nodes, edges, scenarioName.trim());
        return current !== initialSnapshotRef.current;
    }, [nodes, edges, scenarioName]);
    useEffect(() => {
        if (onUnsavedChangesChange) {
            onUnsavedChangesChange(hasUnsavedScenarioChanges, scenarioName.trim());
        }
    }, [hasUnsavedScenarioChanges, scenarioName, onUnsavedChangesChange]);
    useEffect(() => {
        onRegisterSave === null || onRegisterSave === void 0 ? void 0 : onRegisterSave(handleSaveScenario);
    }, [onRegisterSave]);
    // When the component unmounts (or the scenario is closed), reset the unsaved changes flag
    useEffect(() => {
        return () => {
            onUnsavedChangesChange === null || onUnsavedChangesChange === void 0 ? void 0 : onUnsavedChangesChange(false, '');
        };
    }, []);
    // --- Automatic removal of empty dialogue groups ---
    useEffect(() => {
        if (nodes.length === 0)
            return;
        const dialogueHasChildren = new Map();
        // Mark dialogues that have at least one child node (not a group)
        nodes.forEach(n => {
            if (n.type !== 'dialogue-group' && n.data.dialogueId && n.data.dialogueId !== 'standalone') {
                dialogueHasChildren.set(n.data.dialogueId, true);
            }
        });
        const groupsToRemove = [];
        nodes.forEach(n => {
            if (n.type === 'dialogue-group') {
                const dlgId = n.data.dialogueId;
                if (!dialogueHasChildren.get(dlgId)) {
                    groupsToRemove.push(n.id);
                }
            }
        });
        if (groupsToRemove.length > 0) {
            updateNodes(draft => draft.filter(n => !groupsToRemove.includes(n.id)));
            updateEdges(draft => draft.filter(e => !groupsToRemove.includes(e.source) && !groupsToRemove.includes(e.target)));
        }
    }, [nodes, updateEdges, updateNodes]);
    // Early exit after hooks: show loading or error screen
    if (isLoading) {
        return _jsx(LoadingState, {});
    }
    if (error) {
        return _jsx(ErrorState, { message: error });
    }
    // ... existing code ...
    return (_jsxs(NPCsContext.Provider, { value: availableNPCs, children: [_jsxs("div", { className: "scenario-editor-container", children: [_jsxs("div", { className: "scenario-editor-header", children: [_jsxs("div", { className: "scenrario-controls", children: [_jsxs("button", { className: `control-button ${selectedCategory === 'dialogues' ? 'active' : ''}`, onClick: () => handleCategorySelect('dialogues'), title: "Add dialogue nodes", children: [_jsx(FaComments, {}), " Dialogues"] }), _jsxs("button", { className: `control-button ${selectedCategory === 'events' ? 'active' : ''}`, onClick: () => handleCategorySelect('events'), title: "Add event nodes", children: [_jsx(FaBolt, {}), " Events"] }), _jsxs("button", { className: `control-button ${selectedCategory === 'ux' ? 'active' : ''}`, onClick: () => handleCategorySelect('ux'), title: "Add UX nodes", children: [_jsx(FaDesktop, {}), " UX Panels"] }), _jsxs("button", { className: `control-button ${selectedCategory === 'items' ? 'active' : ''}`, onClick: () => handleCategorySelect('items'), title: "Add item nodes", children: [_jsx(FaBox, {}), " Items"] }), _jsxs("button", { className: `control-button ${selectedCategory === 'triggers' ? 'active' : ''}`, onClick: () => handleCategorySelect('triggers'), title: "Add trigger nodes", children: [_jsx(FaBullseye, {}), " Triggers"] })] }), _jsxs("div", { className: "scenario-save-controls", children: [_jsx("input", { type: "text", value: scenarioName, onChange: (e) => setScenarioName(e.target.value), placeholder: "Scenario name...", className: "scenario-name-input", onKeyPress: (e) => {
                                            if (e.key === 'Enter' && scenarioName.trim() && !isSavingScenario) {
                                                handleSaveScenario();
                                            }
                                        } }), _jsxs("button", { className: "cancel-button", onClick: () => { if (onBack)
                                            onBack(); }, disabled: isSavingScenario, title: "Cancel editing", children: [_jsx(FaTimes, {}), " Cancel"] }), _jsx("button", { className: "save-button", onClick: handleSaveScenario, disabled: !scenarioName.trim() || isSavingScenario, title: isSavingScenario ? "Saving scenario..." : "Save scenario", children: isSavingScenario ? 'Saving...' : 'Save Scenario' })] })] }), _jsxs("div", { className: "scenario-editor-content", children: [_jsx("div", { className: `scenario-flow ${!isPropertiesPanelVisible ? 'expanded' : ''}`, children: _jsx("div", { className: "scenario-flow-content", children: _jsx(LevelIdContext.Provider, { value: levelId, children: _jsx(LevelNodesContext.Provider, { value: levelNodes, children: _jsxs(ReactFlow, { nodes: nodes, edges: edges, onNodesChange: onNodesChange, onEdgesChange: onEdgesChange, onConnect: onConnect, onNodeClick: onNodeClick, onNodeDrag: onNodeDrag, onNodeDragStop: onNodeDragStop, nodeTypes: nodeTypes, edgeTypes: edgeTypes, defaultEdgeOptions: { type: 'buttonedge' }, fitView: true, className: "react-flow-canvas", proOptions: { hideAttribution: true }, onDragOver: onDragOver, onDrop: onDrop, minZoom: 0.05, maxZoom: 2, onInit: setReactFlowInstance, children: [_jsx(Controls, {}), _jsx(Background, {})] }) }) }) }) }), _jsx("button", { className: `toggle-properties-button ${!isPropertiesPanelVisible ? 'collapsed' : ''}`, onClick: togglePropertiesPanel, title: isPropertiesPanelVisible ? "Hide properties panel" : "Show properties panel", children: isPropertiesPanelVisible ? _jsx(FaChevronRight, {}) : _jsx(FaChevronLeft, {}) }), isPropertiesPanelVisible && (_jsx("div", { className: "scenario-properties", children: selectedNode ? (
                                // If a node is selected, show its properties
                                _jsxs(_Fragment, { children: [_jsxs("div", { className: `panel-header compact ${selectedCategory === 'dialogues' ? 'dialogues-header' : selectedCategory === 'events' ? 'events-header' : ''}`, children: [getNodeTypeIcon(selectedNode.type), _jsx("span", { className: "panel-title", children: getNodeTypeTitle(selectedNode.type) })] }), selectedNode.type !== 'event' && selectedNode.type !== 'dialogue-group' && selectedNode.type !== 'trigger' && (_jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "nodeText", children: "Text:" }), _jsx("textarea", { id: "nodeText", value: selectedNode.text, onChange: evt => handleUpdateNode({ text: evt.target.value }) })] })), selectedNode.type === 'dialogue-group' && (
                                        // Properties for the dialogue group
                                        _jsxs(_Fragment, { children: [_jsxs("div", { className: "dialogue-group-info", children: [_jsxs("div", { className: "info-item", children: [_jsx("strong", { children: "Dialogue:" }), " ", selectedNode.groupDialogueName || 'Unknown'] }), _jsxs("div", { className: "info-item", children: [_jsx("strong", { children: "NPC Lines:" }), " ", nodes.filter(n => n.type === 'npc' &&
                                                                    n.data.dialogueId === selectedNode.dialogueId).length] }), _jsxs("div", { className: "info-item", children: [_jsx("strong", { children: "Linked to:" }), _jsx("div", { className: "linked-items", children: (() => {
                                                                        const links = [];
                                                                        // Events in the same scenario
                                                                        const eventNodesInScenario = nodes.filter(n => n.type === 'event' && n.data.eventId);
                                                                        if (eventNodesInScenario.length > 0) {
                                                                            const eventNames = eventNodesInScenario.map(n => n.data.text).join(', ');
                                                                            links.push(_jsxs("div", { className: "link-group", children: [_jsx(FaBolt, { className: "link-icon" }), _jsxs("span", { children: ["Events: ", eventNames] })] }, "events"));
                                                                        }
                                                                        // Other dialogues in the scenario
                                                                        const otherDialogueIds = new Set(nodes
                                                                            .filter(n => n.type === 'npc' && n.data.dialogueId !== selectedNode.dialogueId && n.data.dialogueId !== 'standalone')
                                                                            .map(n => n.data.dialogueId));
                                                                        if (otherDialogueIds.size > 0) {
                                                                            const otherDialogueNames = Array.from(otherDialogueIds)
                                                                                .map(dlgId => {
                                                                                const dlg = dialogues.find(d => d.id === dlgId);
                                                                                return dlg ? dlg.name : dlgId;
                                                                            })
                                                                                .join(', ');
                                                                            links.push(_jsxs("div", { className: "link-group", children: [_jsx(FaComments, { className: "link-icon" }), _jsxs("span", { children: ["Dialogues: ", otherDialogueNames] })] }, "dialogues"));
                                                                        }
                                                                        // Scenarios that use this dialogue
                                                                        const dialogueId = selectedNode.dialogueId;
                                                                        const linkedScenarios = scenarios.filter(sc => {
                                                                            var _a;
                                                                            return sc.id !== scenarioId &&
                                                                                ((_a = sc.nodes) === null || _a === void 0 ? void 0 : _a.some((n) => { var _a; return ((_a = n === null || n === void 0 ? void 0 : n.data) === null || _a === void 0 ? void 0 : _a.dialogueId) === dialogueId; }));
                                                                        });
                                                                        if (linkedScenarios.length > 0) {
                                                                            const scenarioNames = linkedScenarios.map(sc => sc.name).join(', ');
                                                                            links.push(_jsxs("div", { className: "link-group", children: [_jsx(FaLink, { className: "link-icon" }), _jsxs("span", { children: ["Scenarios: ", scenarioNames] })] }, "scenarios"));
                                                                        }
                                                                        // Map nodes with random encounters
                                                                        const mapNodeCoords = Object.entries(levelNodes)
                                                                            .filter(([_coord, node]) => {
                                                                            var _a;
                                                                            const re = node.randomEncounters;
                                                                            if (!re || !re.enabled)
                                                                                return false;
                                                                            // Check the events from this dialogue (if any)
                                                                            const dialogueEventIds = events
                                                                                .filter(ev => eventNodesInScenario.some(n => n.data.eventId === ev.id))
                                                                                .map(ev => ev.id);
                                                                            if (re.encounterSelection === 'custom') {
                                                                                return (_a = re.selectedEncounters) === null || _a === void 0 ? void 0 : _a.some((eid) => dialogueEventIds.includes(eid));
                                                                            }
                                                                            else if (re.encounterSelection === 'all') {
                                                                                return dialogueEventIds.some(eid => {
                                                                                    const ev = events.find(e => e.id === eid);
                                                                                    return ev === null || ev === void 0 ? void 0 : ev.isEncounter;
                                                                                });
                                                                            }
                                                                            return false;
                                                                        })
                                                                            .map(([coord]) => coord);
                                                                        if (mapNodeCoords.length > 0) {
                                                                            links.push(_jsxs("div", { className: "link-group", children: [_jsx(FaMap, { className: "link-icon" }), _jsxs("span", { children: ["Map: ", mapNodeCoords.join(', ')] })] }, "map"));
                                                                        }
                                                                        return links.length > 0 ? links : (_jsx("div", { className: "link-group", children: _jsx("span", { className: "no-links", children: "No external links" }) }));
                                                                    })() })] })] }), _jsx("div", { className: "dialogue-group-actions", children: _jsxs("button", { className: "danger-button", onClick: () => {
                                                            if (window.confirm(`Are you sure you want to remove the entire dialogue group "${selectedNode.groupDialogueName}"? This will remove all dialogue nodes and their connections.`)) {
                                                                handleDeleteDialogueGroup(selectedNode.dialogueId);
                                                            }
                                                        }, title: "Remove entire dialogue group", children: [_jsx(FaTrash, {}), " Remove Dialogue Group"] }) })] })), selectedNode.type === 'npc' && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "npcSelect", children: "Select NPC:" }), _jsxs("select", { id: "npcSelect", value: selectedNode.selectedNPCId || '', onChange: (e) => {
                                                                const npcId = e.target.value ? e.target.value : undefined;
                                                                // Update the node data model in React Flow
                                                                handleUpdateNode({ selectedNPCId: npcId, npcImage: undefined }); // Clear the old field
                                                                // Also update the dialogue data model
                                                                updateCurrentDialogue(draft => {
                                                                    if (draft) {
                                                                        const dialogueNode = draft.nodes.find(n => n.id === selectedNode.nodeId);
                                                                        if (dialogueNode) {
                                                                            dialogueNode.selectedNPCId = npcId;
                                                                            dialogueNode.npcImage = undefined; // Clear the old field
                                                                        }
                                                                    }
                                                                });
                                                            }, children: [_jsx("option", { value: "", children: "-- Select NPC --" }), availableNPCs.map((npc) => (_jsx("option", { value: npc.id, children: npc.name }, npc.id)))] })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { children: "Background Image:" }), _jsx(ImageDropzone, { currentImageUrl: selectedNode.backgroundImage || null, onUploadSuccess: (filePath) => {
                                                                // Update the node data model in React Flow
                                                                handleUpdateNode({ backgroundImage: filePath });
                                                                // Also update the dialogue data model
                                                                updateCurrentDialogue(draft => {
                                                                    if (draft) {
                                                                        const dialogueNode = draft.nodes.find(n => n.id === selectedNode.nodeId);
                                                                        if (dialogueNode) {
                                                                            dialogueNode.backgroundImage = filePath;
                                                                        }
                                                                    }
                                                                });
                                                            } })] })] })), selectedNode.type === 'response' && (
                                        // Additional properties for the response node
                                        _jsxs(_Fragment, { children: [_jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "characteristic", children: "Required Characteristic:" }), _jsxs("select", { id: "characteristic", value: selectedNode.ability || '', onChange: (e) => {
                                                                const value = e.target.value ? e.target.value : undefined;
                                                                handleUpdateNode({ ability: value });
                                                            }, children: [_jsx("option", { value: "", children: "None" }), Object.values(AvatarAbility).map((char) => (_jsx("option", { value: char, children: char }, char)))] })] }), selectedNode.ability && (_jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "requiredValue", children: "Required Value:" }), _jsx("input", { id: "requiredValue", type: "number", value: selectedNode.requiredValue || 0, onChange: evt => handleUpdateNode({ requiredValue: parseInt(evt.target.value) }), min: 0 })] })), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "rewardLevel", children: "Reward Level:" }), _jsx("select", { id: "rewardLevel", value: selectedNode.rewardLevel, onChange: evt => handleUpdateNode({ rewardLevel: evt.target.value }), children: Object.values(DialogueRewardLevel).map((level) => (_jsx("option", { value: level, children: level }, level))) })] })] })), selectedNode.type === 'npc' && (
                                        // List of response options for the NPC node
                                        _jsxs("div", { className: "options-list", children: [_jsxs("h4", { children: ["Response Options", _jsx("button", { onClick: handleAddResponseOption, children: "Add Option" })] }), selectedNode.options && selectedNode.options.map((option) => {
                                                    var _a, _b, _c, _d;
                                                    // For dialogue-linked nodes, find the full option data
                                                    let fullOption = null;
                                                    let dialogueNode = null;
                                                    if (currentDialogue && selectedNode.dialogueId === currentDialogue.id) {
                                                        dialogueNode = currentDialogue.nodes.find(n => n.id === selectedNode.nodeId) || null;
                                                        fullOption = ((_a = dialogueNode === null || dialogueNode === void 0 ? void 0 : dialogueNode.responseOptions) === null || _a === void 0 ? void 0 : _a.find(o => o.id === option.id)) || null;
                                                    }
                                                    // For standalone nodes, create a fallback option from the node data
                                                    if (!fullOption) {
                                                        const nodeOption = (_b = selectedNode.options) === null || _b === void 0 ? void 0 : _b.find(o => o.id === option.id);
                                                        fullOption = {
                                                            id: option.id,
                                                            text: option.text,
                                                            requiredCharacteristic: nodeOption === null || nodeOption === void 0 ? void 0 : nodeOption.ability,
                                                            requiredCharacteristicValue: nodeOption === null || nodeOption === void 0 ? void 0 : nodeOption.requiredValue,
                                                            rewardLevel: (nodeOption === null || nodeOption === void 0 ? void 0 : nodeOption.rewardLevel) || DialogueRewardLevel.None
                                                        };
                                                    }
                                                    const optionIndex = (_d = (_c = selectedNode.options) === null || _c === void 0 ? void 0 : _c.indexOf(option)) !== null && _d !== void 0 ? _d : -1;
                                                    return (_jsxs("div", { className: "option-item", children: [_jsxs("div", { className: "option-header", children: [_jsxs("h5", { children: ["Option #", optionIndex + 1] }), _jsx("div", { className: "option-actions", children: _jsx("button", { className: "delete-option", onClick: () => handleDeleteResponseOption(option.id), title: "Delete option", children: _jsx(FaTrash, {}) }) })] }), _jsxs("div", { className: "option-form", children: [_jsxs("div", { className: "form-group", children: [_jsx("label", { children: "Text:" }), _jsx("textarea", { value: fullOption.text, onChange: (e) => {
                                                                                    var _a;
                                                                                    const newText = e.target.value;
                                                                                    // Update the option via a dedicated function
                                                                                    const updatedOptions = ((_a = selectedNode.options) === null || _a === void 0 ? void 0 : _a.map(opt => opt.id === option.id ? Object.assign(Object.assign({}, opt), { text: newText }) : opt)) || [];
                                                                                    // Use handleUpdateNode like for event nodes - it updates everything itself
                                                                                    handleUpdateNode({ options: updatedOptions });
                                                                                } })] }), _jsxs("div", { className: "option-row", children: [_jsxs("div", { className: "form-group", children: [_jsx("label", { children: "Characteristic:" }), _jsxs("select", { value: fullOption.requiredCharacteristic || '', onChange: (e) => {
                                                                                            var _a;
                                                                                            const newCharacteristic = e.target.value ? e.target.value : undefined;
                                                                                            // Update the option via handleUpdateNode
                                                                                            const updatedOptions = ((_a = selectedNode.options) === null || _a === void 0 ? void 0 : _a.map(opt => opt.id === option.id ? Object.assign(Object.assign({}, opt), { ability: newCharacteristic }) : opt)) || [];
                                                                                            handleUpdateNode({ options: updatedOptions });
                                                                                            // Update selectedNode
                                                                                            setSelectedNode(prev => {
                                                                                                if (prev && prev.options) {
                                                                                                    const updatedOptions = prev.options.map(o => o.id === option.id ? Object.assign(Object.assign({}, o), { ability: newCharacteristic }) : o);
                                                                                                    return Object.assign(Object.assign({}, prev), { options: updatedOptions });
                                                                                                }
                                                                                                return prev;
                                                                                            });
                                                                                            // Update the dialogue model if the node is linked to a dialogue
                                                                                            if (currentDialogue && selectedNode.dialogueId === currentDialogue.id) {
                                                                                                updateCurrentDialogue(draft => {
                                                                                                    if (draft) {
                                                                                                        const node = draft.nodes.find(n => n.id === selectedNode.nodeId);
                                                                                                        if (node && node.responseOptions) {
                                                                                                            const opt = node.responseOptions.find(o => o.id === option.id);
                                                                                                            if (opt) {
                                                                                                                opt.requiredCharacteristic = newCharacteristic;
                                                                                                            }
                                                                                                        }
                                                                                                    }
                                                                                                });
                                                                                            }
                                                                                            // Notify ReactFlow of the change to the node's internal structure
                                                                                            updateNodeInternals(selectedNode.id);
                                                                                        }, children: [_jsx("option", { value: "", children: "None" }), Object.values(AvatarAbility).map((char) => (_jsx("option", { value: char, children: char }, char)))] })] }), fullOption.requiredCharacteristic && (_jsxs("div", { className: "form-group", children: [_jsx("label", { children: "Required Value:" }), _jsx("input", { type: "number", value: fullOption.requiredCharacteristicValue || 0, onChange: (e) => {
                                                                                            var _a;
                                                                                            const newValue = parseInt(e.target.value);
                                                                                            // Update the option via handleUpdateNode
                                                                                            const updatedOptions = ((_a = selectedNode.options) === null || _a === void 0 ? void 0 : _a.map(opt => opt.id === option.id ? Object.assign(Object.assign({}, opt), { requiredValue: newValue }) : opt)) || [];
                                                                                            handleUpdateNode({ options: updatedOptions });
                                                                                            // Update selectedNode
                                                                                            setSelectedNode(prev => {
                                                                                                if (prev && prev.options) {
                                                                                                    const updatedOptions = prev.options.map(o => o.id === option.id ? Object.assign(Object.assign({}, o), { requiredValue: newValue }) : o);
                                                                                                    return Object.assign(Object.assign({}, prev), { options: updatedOptions });
                                                                                                }
                                                                                                return prev;
                                                                                            });
                                                                                            // Update the dialogue model if the node is linked to a dialogue
                                                                                            if (currentDialogue && selectedNode.dialogueId === currentDialogue.id) {
                                                                                                updateCurrentDialogue(draft => {
                                                                                                    if (draft) {
                                                                                                        const node = draft.nodes.find(n => n.id === selectedNode.nodeId);
                                                                                                        if (node && node.responseOptions) {
                                                                                                            const opt = node.responseOptions.find(o => o.id === option.id);
                                                                                                            if (opt) {
                                                                                                                opt.requiredCharacteristicValue = newValue;
                                                                                                            }
                                                                                                        }
                                                                                                    }
                                                                                                });
                                                                                            }
                                                                                            // Notify ReactFlow of the change to the node's internal structure
                                                                                            updateNodeInternals(selectedNode.id);
                                                                                        }, min: 0 })] }))] }), _jsx("div", { className: "option-row", children: _jsxs("div", { className: "form-group", children: [_jsx("label", { children: "Reward Level:" }), _jsx("select", { value: fullOption.rewardLevel, onChange: (e) => {
                                                                                        var _a;
                                                                                        const newReward = e.target.value;
                                                                                        // Update the option via handleUpdateNode
                                                                                        const updatedOptions = ((_a = selectedNode.options) === null || _a === void 0 ? void 0 : _a.map(opt => opt.id === option.id ? Object.assign(Object.assign({}, opt), { rewardLevel: newReward }) : opt)) || [];
                                                                                        handleUpdateNode({ options: updatedOptions });
                                                                                        // Update selectedNode
                                                                                        setSelectedNode(prev => {
                                                                                            if (prev && prev.options) {
                                                                                                const updatedOptions = prev.options.map(o => o.id === option.id ? Object.assign(Object.assign({}, o), { rewardLevel: newReward }) : o);
                                                                                                return Object.assign(Object.assign({}, prev), { options: updatedOptions });
                                                                                            }
                                                                                            return prev;
                                                                                        });
                                                                                        // Update the dialogue model if the node is linked to a dialogue
                                                                                        if (currentDialogue && selectedNode.dialogueId === currentDialogue.id) {
                                                                                            updateCurrentDialogue(draft => {
                                                                                                if (draft) {
                                                                                                    const node = draft.nodes.find(n => n.id === selectedNode.nodeId);
                                                                                                    if (node && node.responseOptions) {
                                                                                                        const opt = node.responseOptions.find(o => o.id === option.id);
                                                                                                        if (opt) {
                                                                                                            opt.rewardLevel = newReward;
                                                                                                        }
                                                                                                    }
                                                                                                }
                                                                                            });
                                                                                        }
                                                                                        // Notify ReactFlow of the change to the node's internal structure
                                                                                        updateNodeInternals(selectedNode.id);
                                                                                    }, children: Object.values(DialogueRewardLevel).map((level) => (_jsx("option", { value: level, children: level }, level))) })] }) })] })] }, option.id));
                                                })] })), selectedNode.type !== 'event' && selectedNode.type !== 'dialogue-group' && selectedNode.type !== 'trigger' && (_jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "nodeText", children: "Text:" }), _jsx("textarea", { id: "nodeText", value: selectedNode.text, onChange: (e) => handleUpdateNode({ text: e.target.value }), rows: 4, style: { fontSize: '12px' } })] })), selectedNode.type === 'event' && selectedNode.eventData && (
                                        // Properties for editing the event
                                        _jsxs(_Fragment, { children: [_jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "eventTitle", children: "Event Title:" }), _jsx("input", { id: "eventTitle", type: "text", value: selectedNode.eventData.title, onChange: (e) => {
                                                                const newTitle = e.target.value;
                                                                handleUpdateNode({
                                                                    text: newTitle,
                                                                    eventData: Object.assign(Object.assign({}, selectedNode.eventData), { title: newTitle })
                                                                });
                                                            } })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "eventDescription", children: "Description:" }), _jsx("textarea", { id: "eventDescription", value: selectedNode.eventData.description, onChange: (e) => {
                                                                const newDescription = e.target.value;
                                                                handleUpdateNode({
                                                                    eventData: Object.assign(Object.assign({}, selectedNode.eventData), { description: newDescription })
                                                                });
                                                            }, rows: 4, style: { fontSize: '12px' } })] }), _jsx("div", { className: "form-group", children: _jsxs("div", { className: "checkbox-group", children: [_jsx("input", { type: "checkbox", id: "isEncounter", checked: selectedNode.eventData.isEncounter, onChange: evt => {
                                                                    handleUpdateNode({
                                                                        eventData: Object.assign(Object.assign({}, selectedNode.eventData), { isEncounter: evt.target.checked })
                                                                    });
                                                                } }), _jsx("label", { htmlFor: "isEncounter", children: "This is a random encounter" })] }) }), _jsxs("div", { className: "images-row", children: [_jsxs("div", { className: "form-group", children: [_jsx("label", { children: "Background Image:" }), _jsxs("div", { className: "image-dropzone-with-delete", children: [_jsx(ImageDropzone, { currentImageUrl: selectedNode.eventData.backgroundImage || null, onUploadSuccess: filePath => {
                                                                                handleUpdateNode({
                                                                                    eventImage: filePath,
                                                                                    eventData: Object.assign(Object.assign({}, selectedNode.eventData), { backgroundImage: filePath })
                                                                                });
                                                                            } }), selectedNode.eventData.backgroundImage && (_jsx("button", { type: "button", className: "delete-image-button", onClick: () => {
                                                                                handleUpdateNode({
                                                                                    eventImage: undefined,
                                                                                    eventData: Object.assign(Object.assign({}, selectedNode.eventData), { backgroundImage: undefined })
                                                                                });
                                                                            }, title: "Delete background image", children: _jsx(FaTrash, {}) }))] })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { children: "Card Image:" }), _jsxs("div", { className: "image-dropzone-with-delete", children: [_jsx(ImageDropzone, { currentImageUrl: selectedNode.eventData.cardImage || null, onUploadSuccess: filePath => {
                                                                                handleUpdateNode({
                                                                                    eventData: Object.assign(Object.assign({}, selectedNode.eventData), { cardImage: filePath })
                                                                                });
                                                                            } }), selectedNode.eventData.cardImage && (_jsx("button", { type: "button", className: "delete-image-button", onClick: () => {
                                                                                handleUpdateNode({
                                                                                    eventData: Object.assign(Object.assign({}, selectedNode.eventData), { cardImage: undefined })
                                                                                });
                                                                            }, title: "Delete card image", children: _jsx(FaTrash, {}) }))] })] })] }), _jsxs("div", { className: "event-encounters", children: [_jsx("h4", { children: "Challenge Options" }), selectedNode.eventData.encounters.map((encounter, encounterIndex) => (_jsxs("div", { className: "encounter-card", style: {
                                                                border: `2px solid ${ABILITY_COLORS[encounter.ability]}`,
                                                                borderRadius: '8px',
                                                                padding: '12px',
                                                                marginBottom: '12px'
                                                            }, children: [_jsxs("div", { className: "form-group", children: [_jsx("label", { children: "Characteristic:" }), _jsx("select", { value: encounter.ability, onChange: evt => {
                                                                                const updatedEncounters = selectedNode.eventData.encounters.map((enc, idx) => idx === encounterIndex ? Object.assign(Object.assign({}, enc), { ability: evt.target.value }) : enc);
                                                                                handleUpdateNode({
                                                                                    eventData: Object.assign(Object.assign({}, selectedNode.eventData), { encounters: updatedEncounters })
                                                                                });
                                                                            }, style: { backgroundColor: ABILITY_COLORS[encounter.ability] }, children: Object.values(AvatarAbility).map(ability => (_jsx("option", { value: ability, children: ability }, ability))) })] }), _jsxs("div", { className: "parameter-row", children: [_jsxs("div", { className: "form-group", children: [_jsx("label", { children: "Difficulty:" }), _jsx("input", { type: "number", min: "1", max: "20", value: encounter.difficulty, onChange: (e) => {
                                                                                        const updatedEncounters = selectedNode.eventData.encounters.map((enc, idx) => idx === encounterIndex ? Object.assign(Object.assign({}, enc), { difficulty: parseInt(e.target.value) }) : enc);
                                                                                        handleUpdateNode({ eventData: Object.assign(Object.assign({}, selectedNode.eventData), { encounters: updatedEncounters }) });
                                                                                    } })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { children: "Power Cost:" }), _jsx("input", { type: "number", min: "0", value: encounter.powerCost, onChange: (e) => {
                                                                                        const updatedEncounters = selectedNode.eventData.encounters.map((enc, idx) => idx === encounterIndex ? Object.assign(Object.assign({}, enc), { powerCost: parseInt(e.target.value) }) : enc);
                                                                                        handleUpdateNode({ eventData: Object.assign(Object.assign({}, selectedNode.eventData), { encounters: updatedEncounters }) });
                                                                                    } })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { children: "Failure Penalty:" }), _jsx("input", { type: "number", min: "0", value: encounter.failurePenalty, onChange: (e) => {
                                                                                        const updatedEncounters = selectedNode.eventData.encounters.map((enc, idx) => idx === encounterIndex ? Object.assign(Object.assign({}, enc), { failurePenalty: parseInt(e.target.value) }) : enc);
                                                                                        handleUpdateNode({ eventData: Object.assign(Object.assign({}, selectedNode.eventData), { encounters: updatedEncounters }) });
                                                                                    } })] })] }), _jsx("div", { className: "reward-row", children: _jsxs("div", { className: "form-group", children: [_jsx("label", { children: "Reward Amount:" }), _jsx("input", { type: "number", min: "0", value: encounter.rewardAmount || 1, onChange: (e) => {
                                                                                    const updatedEncounters = selectedNode.eventData.encounters.map((enc, idx) => idx === encounterIndex ? Object.assign(Object.assign({}, enc), { rewardAmount: parseInt(e.target.value), rewardType: PrizeType.Knowledge, rewardResourceName: undefined // Clear the resource
                                                                                     }) : enc);
                                                                                    handleUpdateNode({
                                                                                        eventData: Object.assign(Object.assign({}, selectedNode.eventData), { encounters: updatedEncounters })
                                                                                    });
                                                                                } })] }) }), _jsxs("div", { className: "outcome-texts", children: [_jsxs("div", { className: "form-group", children: [_jsx("label", { style: { color: 'var(--success-color, #27ae60)' }, children: "Success Text:" }), " ", _jsx("textarea", { value: encounter.successText, onChange: (e) => {
                                                                                        const updatedEncounters = selectedNode.eventData.encounters.map((enc, idx) => idx === encounterIndex ? Object.assign(Object.assign({}, enc), { successText: e.target.value }) : enc);
                                                                                        handleUpdateNode({ eventData: Object.assign(Object.assign({}, selectedNode.eventData), { encounters: updatedEncounters }) });
                                                                                    }, rows: 3, placeholder: "Text shown on successful challenge completion" })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { style: { color: 'var(--failure-color, #e74c3c)' }, children: "Failure Text:" }), " ", _jsx("textarea", { value: encounter.failureText, onChange: (e) => {
                                                                                        const updatedEncounters = selectedNode.eventData.encounters.map((enc, idx) => idx === encounterIndex ? Object.assign(Object.assign({}, enc), { failureText: e.target.value }) : enc);
                                                                                        handleUpdateNode({ eventData: Object.assign(Object.assign({}, selectedNode.eventData), { encounters: updatedEncounters }) });
                                                                                    }, rows: 3, placeholder: "Text shown on failed challenge" })] })] })] }, encounterIndex)))] })] })), (selectedNode.type === 'item-reward' || selectedNode.type === 'item-requirement' || selectedNode.type === 'item-spent') && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "itemNodeText", children: "Text:" }), _jsx("textarea", { id: "itemNodeText", value: selectedNode.text, onChange: (e) => handleUpdateNode({ text: e.target.value }), rows: 3, placeholder: "Enter description for this item action..." })] }), _jsxs("div", { className: "items-list-section", children: [_jsxs("div", { className: "section-header", children: [_jsx("label", { children: "Items:" }), _jsxs("button", { type: "button", className: "add-item-button", onClick: () => {
                                                                        const currentItems = selectedNode.items || [];
                                                                        const newItems = [...currentItems, { itemId: '', quantity: 1 }];
                                                                        handleUpdateNode({ items: newItems });
                                                                    }, disabled: availableItems.length === 0, children: [_jsx(FaPlus, {}), " Add Item"] })] }), (selectedNode.items || []).length === 0 ? (_jsx("div", { className: "no-items-message", children: "No items added yet. Click \"Add Item\" to start." })) : (_jsx("div", { className: "items-list", children: (selectedNode.items || []).map((item, index) => (_jsxs("div", { className: "item-row", children: [_jsxs("div", { className: "item-fields", children: [_jsx("div", { className: "item-select-wrapper", children: _jsxs("select", { value: item.itemId, onChange: (e) => {
                                                                                        const currentItems = selectedNode.items || [];
                                                                                        const newItems = [...currentItems];
                                                                                        newItems[index] = Object.assign(Object.assign({}, newItems[index]), { itemId: e.target.value });
                                                                                        handleUpdateNode({ items: newItems });
                                                                                    }, children: [_jsx("option", { value: "", children: "-- Select Item --" }), availableItems.map(availableItem => (_jsx("option", { value: availableItem.id, children: availableItem.name }, availableItem.id)))] }) }), _jsxs("div", { className: "quantity-wrapper", children: [_jsx("label", { children: "Quantity:" }), _jsx("input", { type: "number", min: "1", value: item.quantity, onChange: (e) => {
                                                                                            const currentItems = selectedNode.items || [];
                                                                                            const newItems = [...currentItems];
                                                                                            newItems[index] = Object.assign(Object.assign({}, newItems[index]), { quantity: parseInt(e.target.value) || 1 });
                                                                                            handleUpdateNode({ items: newItems });
                                                                                        }, placeholder: "Enter quantity" })] })] }), _jsxs("button", { type: "button", className: "remove-item-button", onClick: () => {
                                                                            const currentItems = selectedNode.items || [];
                                                                            const newItems = currentItems.filter((_, i) => i !== index);
                                                                            handleUpdateNode({ items: newItems });
                                                                        }, title: "Remove item", children: [_jsx(FaTrash, {}), " Remove"] })] }, index))) }))] })] })), selectedNode.type === 'trigger' && (_jsx(_Fragment, { children: selectedNode.triggerType === 'conditional' && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "trigger-group-section", children: [_jsxs("div", { className: "section-header", children: [_jsx("div", { children: _jsx("label", { children: "Trigger Type:" }) }), _jsx("div", { className: "help-text", children: "Select the type of condition that will activate this trigger. Only one type can be active at a time." })] }), _jsx("div", { className: "trigger-group-selector", children: _jsxs("div", { className: "trigger-group-options", children: [_jsxs("div", { className: `trigger-group-option ${(selectedNode.triggerGroupType || 'MapPoint') === 'MapPoint' ? 'active' : ''}`, onClick: () => {
                                                                                handleUpdateNode({
                                                                                    triggerGroupType: 'MapPoint',
                                                                                    triggerMapPoint: selectedNode.triggerMapPoint || {
                                                                                        nodeCode: '',
                                                                                        visitType: 'OnVisit'
                                                                                    }
                                                                                });
                                                                            }, children: [_jsx("div", { className: "trigger-group-icon map-trigger", children: _jsx(FaMapMarkerAlt, {}) }), _jsxs("div", { className: "trigger-group-content", children: [_jsx("div", { className: "trigger-group-title", children: "Map Point Trigger" }), _jsx("div", { className: "trigger-group-description", children: "Trigger based on map location visits" })] })] }), _jsxs("div", { className: `trigger-group-option ${selectedNode.triggerGroupType === 'HealthStatus' ? 'active' : ''}`, onClick: () => {
                                                                                handleUpdateNode({
                                                                                    triggerGroupType: 'HealthStatus',
                                                                                    triggerHealthStatus: selectedNode.triggerHealthStatus || {
                                                                                        type: 'OnHealthBelow',
                                                                                        healthValue: 30
                                                                                    }
                                                                                });
                                                                            }, children: [_jsx("div", { className: "trigger-group-icon health-trigger", children: _jsx(FaHeartbeat, {}) }), _jsxs("div", { className: "trigger-group-content", children: [_jsx("div", { className: "trigger-group-title", children: "Health Status" }), _jsx("div", { className: "trigger-group-description", children: "Trigger based on health conditions" })] })] }), _jsxs("div", { className: `trigger-group-option ${selectedNode.triggerGroupType === 'Time' ? 'active' : ''}`, onClick: () => {
                                                                                handleUpdateNode({
                                                                                    triggerGroupType: 'Time',
                                                                                    triggerTime: selectedNode.triggerTime || {
                                                                                        type: 'OnTurn',
                                                                                        value: '15',
                                                                                        description: 'Turn number 15 occurred'
                                                                                    }
                                                                                });
                                                                            }, children: [_jsx("div", { className: "trigger-group-icon time-trigger", children: _jsx(FaClock, {}) }), _jsxs("div", { className: "trigger-group-content", children: [_jsx("div", { className: "trigger-group-title", children: "Time" }), _jsx("div", { className: "trigger-group-description", children: "Trigger based on time or turn conditions" })] })] })] }) }), (selectedNode.triggerGroupType || 'MapPoint') === 'MapPoint' && (_jsxs("div", { className: "trigger-config-section map-point-config", children: [_jsx("div", { className: "section-subheader", children: _jsx("label", { children: "Map Point Configuration:" }) }), _jsxs("div", { className: "trigger-config-form", children: [_jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "mapNodeCode", children: "Map Node Code:" }), _jsx("div", { className: "map-node-picker", children: _jsxs("div", { className: "map-node-input", onClick: () => setIsMapPickerOpen(true), children: [_jsx("span", { className: "selected-node-display", children: ((_b = selectedNode.triggerMapPoint) === null || _b === void 0 ? void 0 : _b.nodeCode) || 'Click to select map node...' }), _jsx("div", { className: "picker-arrow", children: _jsx(FaChevronDown, {}) })] }) })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "visitType", children: "Visit Type:" }), _jsxs("select", { id: "visitType", value: ((_c = selectedNode.triggerMapPoint) === null || _c === void 0 ? void 0 : _c.visitType) || 'OnVisit', onChange: (e) => {
                                                                                            var _a;
                                                                                            handleUpdateNode({
                                                                                                triggerMapPoint: {
                                                                                                    nodeCode: ((_a = selectedNode.triggerMapPoint) === null || _a === void 0 ? void 0 : _a.nodeCode) || '',
                                                                                                    visitType: e.target.value
                                                                                                }
                                                                                            });
                                                                                        }, children: [_jsx("option", { value: "OnVisit", children: "On Visit" }), _jsx("option", { value: "OnFirstVisit", children: "On First Visit" }), _jsx("option", { value: "OnLeave", children: "On Leave" })] })] })] })] })), selectedNode.triggerGroupType === 'HealthStatus' && (_jsxs("div", { className: "trigger-config-section health-status-config", children: [_jsx("div", { className: "section-subheader", children: _jsx("label", { children: "Health Status Configuration:" }) }), _jsxs("div", { className: "trigger-config-form", children: [_jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "healthType", children: "Health Condition:" }), _jsxs("select", { id: "healthType", value: ((_d = selectedNode.triggerHealthStatus) === null || _d === void 0 ? void 0 : _d.type) || 'OnHealthBelow', onChange: (e) => {
                                                                                            var _a;
                                                                                            handleUpdateNode({
                                                                                                triggerHealthStatus: {
                                                                                                    type: e.target.value,
                                                                                                    healthValue: ((_a = selectedNode.triggerHealthStatus) === null || _a === void 0 ? void 0 : _a.healthValue) || 30
                                                                                                }
                                                                                            });
                                                                                        }, children: [_jsx("option", { value: "OnHealthBelow", children: "Health Below" }), _jsx("option", { value: "OnHealthAbove", children: "Health Above" }), _jsx("option", { value: "OnHealthEquals", children: "Health Equals" })] })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "healthValue", children: "Health Value (%):" }), _jsx("input", { type: "number", id: "healthValue", min: "0", max: "100", value: ((_e = selectedNode.triggerHealthStatus) === null || _e === void 0 ? void 0 : _e.healthValue) || 30, onChange: (e) => {
                                                                                            var _a;
                                                                                            handleUpdateNode({
                                                                                                triggerHealthStatus: {
                                                                                                    type: ((_a = selectedNode.triggerHealthStatus) === null || _a === void 0 ? void 0 : _a.type) || 'OnHealthBelow',
                                                                                                    healthValue: parseInt(e.target.value) || 0
                                                                                                }
                                                                                            });
                                                                                        } })] })] })] })), selectedNode.triggerGroupType === 'Time' && (_jsxs("div", { className: "trigger-config-section time-config", children: [_jsx("div", { className: "section-subheader", children: _jsx("label", { children: "Time Configuration:" }) }), _jsxs("div", { className: "trigger-config-form", children: [_jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "timeType", children: "Time Condition:" }), _jsxs("select", { id: "timeType", value: ((_f = selectedNode.triggerTime) === null || _f === void 0 ? void 0 : _f.type) || 'OnTurn', onChange: (e) => {
                                                                                            const isOnTurn = e.target.value === 'OnTurn';
                                                                                            handleUpdateNode({
                                                                                                triggerTime: {
                                                                                                    type: e.target.value,
                                                                                                    value: isOnTurn ? '15' : 'Explode',
                                                                                                    description: isOnTurn ? 'Turn number 15 occurred' : 'The specified timer expired'
                                                                                                }
                                                                                            });
                                                                                        }, children: [_jsx("option", { value: "OnTurn", children: "On Turn" }), _jsx("option", { value: "OnTimerExpired", children: "Timer Expired" })] })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "timeValue", children: ((_g = selectedNode.triggerTime) === null || _g === void 0 ? void 0 : _g.type) === 'OnTurn' ? 'Turn Number:' : 'Timer Name:' }), _jsx("input", { type: "text", id: "timeValue", value: ((_h = selectedNode.triggerTime) === null || _h === void 0 ? void 0 : _h.value) || '', onChange: (e) => {
                                                                                            var _a, _b;
                                                                                            const isOnTurn = ((_a = selectedNode.triggerTime) === null || _a === void 0 ? void 0 : _a.type) === 'OnTurn';
                                                                                            handleUpdateNode({
                                                                                                triggerTime: {
                                                                                                    type: ((_b = selectedNode.triggerTime) === null || _b === void 0 ? void 0 : _b.type) || 'OnTurn',
                                                                                                    value: e.target.value,
                                                                                                    description: isOnTurn
                                                                                                        ? `Turn number ${e.target.value} occurred`
                                                                                                        : 'The specified timer expired'
                                                                                                }
                                                                                            });
                                                                                        }, placeholder: ((_j = selectedNode.triggerTime) === null || _j === void 0 ? void 0 : _j.type) === 'OnTurn' ? 'e.g., 15' : 'e.g., Explode' })] }), ((_k = selectedNode.triggerTime) === null || _k === void 0 ? void 0 : _k.description) && (_jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "timeDescription", children: "Description:" }), _jsx("input", { type: "text", id: "timeDescription", value: selectedNode.triggerTime.description, onChange: (e) => {
                                                                                            handleUpdateNode({
                                                                                                triggerTime: Object.assign(Object.assign({}, selectedNode.triggerTime), { description: e.target.value })
                                                                                            });
                                                                                        }, placeholder: "Optional description for UI display" })] }))] })] }))] }), _jsxs("div", { className: "condition-groups-section", children: [_jsxs("div", { className: "section-header", children: [_jsxs("div", { children: [_jsx("label", { children: "Condition Groups:" }), _jsxs("button", { type: "button", className: "add-condition-button-panel", onClick: () => {
                                                                                    // Create a new condition group with preset colors and names
                                                                                    const defaultColors = ['#22c55e', '#ef4444', '#eab308']; // Green, Red, Yellow
                                                                                    const defaultNames = ['Success', 'Failure', 'Partial']; // Default names
                                                                                    const currentGroupCount = (selectedNode.triggerConditionGroups || []).length;
                                                                                    const newColor = currentGroupCount < defaultColors.length
                                                                                        ? defaultColors[currentGroupCount]
                                                                                        : '#ef4444'; // If more than 3 groups, red by default
                                                                                    const newName = currentGroupCount < defaultNames.length
                                                                                        ? defaultNames[currentGroupCount]
                                                                                        : `Group ${(selectedNode.triggerConditionGroups || []).length + 1}`;
                                                                                    const newGroup = {
                                                                                        id: `group_${uuidv4()}`,
                                                                                        name: newName,
                                                                                        color: newColor,
                                                                                        mode: 'once', // Triggers once by default
                                                                                        conditions: [],
                                                                                        position: { x: 20, y: 20 }
                                                                                    };
                                                                                    const groups = selectedNode.triggerConditionGroups || [];
                                                                                    const newGroups = [...groups, newGroup];
                                                                                    handleUpdateNode({ triggerConditionGroups: newGroups });
                                                                                }, children: [_jsx(FaPlus, {}), " Add Group"] })] }), _jsx("div", { className: "help-text", children: "Create different groups of conditions. Each group can have its own color and conditions." })] }), (selectedNode.triggerConditionGroups || []).length === 0 ? (_jsx("div", { className: "no-conditions-message", children: "No condition groups created yet. Click \"Add Group\" to start." })) : (_jsx("div", { className: "condition-groups-management", children: (selectedNode.triggerConditionGroups || []).map((group, groupIndex) => (_jsxs("div", { className: "condition-group-card", style: { borderLeftColor: group.color }, ref: el => { conditionGroupRefs.current[group.id] = el; }, children: [_jsxs("div", { className: "group-header-row", children: [_jsx("input", { type: "text", className: "group-name-input", value: group.name, onChange: (e) => {
                                                                                        const groups = selectedNode.triggerConditionGroups || [];
                                                                                        const newGroups = [...groups];
                                                                                        newGroups[groupIndex] = Object.assign(Object.assign({}, newGroups[groupIndex]), { name: e.target.value });
                                                                                        handleUpdateNode({ triggerConditionGroups: newGroups });
                                                                                    }, placeholder: "Group name" }), _jsxs("div", { className: "group-color-dropdown", children: [_jsx("div", { className: "color-picker-trigger", style: { backgroundColor: group.color }, onClick: () => {
                                                                                                const dropdown = document.getElementById(`color-dropdown-${groupIndex}`);
                                                                                                if (dropdown) {
                                                                                                    dropdown.style.display = dropdown.style.display === 'block' ? 'none' : 'block';
                                                                                                }
                                                                                            }, children: _jsx(FaChevronDown, {}) }), _jsx("div", { id: `color-dropdown-${groupIndex}`, className: "color-picker-dropdown", style: { display: 'none' }, children: _jsx("div", { className: "color-options-grid", children: ['#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#6366f1', '#d946ef', '#78716c', '#525252'].map(color => (_jsx("div", { className: `color-option ${group.color === color ? 'selected' : ''}`, style: { backgroundColor: color }, onClick: () => {
                                                                                                        const groups = selectedNode.triggerConditionGroups || [];
                                                                                                        const newGroups = [...groups];
                                                                                                        newGroups[groupIndex] = Object.assign(Object.assign({}, newGroups[groupIndex]), { color });
                                                                                                        handleUpdateNode({ triggerConditionGroups: newGroups });
                                                                                                        // Close dropdown
                                                                                                        const dropdown = document.getElementById(`color-dropdown-${groupIndex}`);
                                                                                                        if (dropdown) {
                                                                                                            dropdown.style.display = 'none';
                                                                                                        }
                                                                                                    } }, color))) }) })] }), _jsx("button", { type: "button", className: "remove-group-button", onClick: () => {
                                                                                        const groups = selectedNode.triggerConditionGroups || [];
                                                                                        const newGroups = groups.filter((_, i) => i !== groupIndex);
                                                                                        handleUpdateNode({ triggerConditionGroups: newGroups });
                                                                                    }, title: "Remove group", children: _jsx(FaTrash, {}) })] }), _jsxs("div", { className: "group-mode-select", children: [_jsx("label", { style: { fontSize: '12px', marginBottom: '6px', display: 'block', color: 'rgba(255, 255, 255, 0.8)' }, children: "Trigger Behavior:" }), _jsxs("div", { className: "mode-toggle-group", children: [_jsxs("button", { type: "button", className: `mode-toggle-option mode-once ${(group.mode || 'once') === 'once' ? 'active' : ''}`, onClick: () => {
                                                                                                const groups = selectedNode.triggerConditionGroups || [];
                                                                                                const newGroups = [...groups];
                                                                                                newGroups[groupIndex] = Object.assign(Object.assign({}, newGroups[groupIndex]), { mode: 'once' });
                                                                                                handleUpdateNode({ triggerConditionGroups: newGroups });
                                                                                            }, children: [_jsx(FaBullseye, { style: { marginRight: '6px' } }), "One-time"] }), _jsxs("button", { type: "button", className: `mode-toggle-option mode-repeatable ${group.mode === 'repeatable' ? 'active' : ''}`, onClick: () => {
                                                                                                const groups = selectedNode.triggerConditionGroups || [];
                                                                                                const newGroups = [...groups];
                                                                                                newGroups[groupIndex] = Object.assign(Object.assign({}, newGroups[groupIndex]), { mode: 'repeatable' });
                                                                                                handleUpdateNode({ triggerConditionGroups: newGroups });
                                                                                            }, children: [_jsx(FaGamepad, { style: { marginRight: '6px' } }), "Repeatable"] })] })] }), _jsxs("div", { className: "group-conditions-management", children: [_jsxs("button", { type: "button", className: "add-condition-button", onClick: () => {
                                                                                        // Determine the condition types already used in the group
                                                                                        const usedTypes = group.conditions.map(c => c.type);
                                                                                        const availableTypes = ['item-requirement', 'scenario-status', 'game-phase', 'mission-status', 'task-status'];
                                                                                        const firstAvailableType = availableTypes.find(type => !usedTypes.includes(type)) || 'item-requirement';
                                                                                        const newCondition = {
                                                                                            id: `condition_${uuidv4()}`,
                                                                                            type: firstAvailableType,
                                                                                            items: firstAvailableType === 'item-requirement' ? [] : undefined,
                                                                                            position: { x: 0, y: 0 }
                                                                                        };
                                                                                        const groups = selectedNode.triggerConditionGroups || [];
                                                                                        const newGroups = [...groups];
                                                                                        newGroups[groupIndex] = Object.assign(Object.assign({}, newGroups[groupIndex]), { conditions: [...newGroups[groupIndex].conditions, newCondition] });
                                                                                        handleUpdateNode({ triggerConditionGroups: newGroups });
                                                                                    }, disabled: !availableItems.length, children: [_jsx(FaPlus, {}), " Add Condition"] }), group.conditions.length === 0 ? (_jsx("div", { className: "no-conditions-message", children: "No conditions configured." })) : (_jsx("div", { className: "conditions-list", children: group.conditions.map((condition, conditionIndex) => {
                                                                                        // Get the condition types already used in this group
                                                                                        const usedConditionTypes = group.conditions.map(c => c.type);
                                                                                        return (_jsx("div", { ref: el => { conditionItemRefs.current[condition.id] = el; }, children: _jsx(ConditionEditor, { condition: condition, conditionIndex: conditionIndex, groupIndex: groupIndex, availableItems: availableRequirements, itemImages: itemImages, usedConditionTypes: usedConditionTypes, availableScenarios: scenarios.filter(s => s.id !== scenarioId).map(s => ({ id: s.id, name: s.name })), onUpdateCondition: handleUpdateCondition, onRemoveCondition: handleRemoveCondition }) }, condition.id));
                                                                                    }) }))] })] }, group.id))) }))] })] })) }))] })) : selectedCategory ? (
                                // If a category is selected, show the category content
                                _jsxs("div", { className: "category-content", children: [_jsx("div", { className: `panel-header compact ${selectedCategory === 'dialogues' ? 'dialogues-header' : selectedCategory === 'events' ? 'events-header' : ''}`, children: selectedCategory === 'dialogues' ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: "dialogues-header-top", children: [getCategoryIcon(selectedCategory), _jsx("span", { className: "panel-title", children: getCategoryTitle(selectedCategory) }), _jsxs("div", { className: "search-input-wrapper", children: [_jsx(FaSearch, { className: "search-icon" }), _jsx("input", { type: "text", placeholder: "Search dialogues...", value: dialoguesSearchTerm, onChange: e => handleDialoguesSearchChange(e.target.value), className: "search-input" }), dialoguesSearchTerm && (_jsx("button", { className: "clear-search-button", onClick: () => handleDialoguesSearchChange(''), children: _jsx(FaTimes, {}) }))] })] }), _jsx("div", { className: "visual-separator" }), _jsx("div", { className: "filter-section", children: _jsxs("div", { className: "dialogue-types-buttons", children: [availableDialogueTypes.map(type => (_jsx("button", { className: `dialogue-type-filter-button ${selectedDialogueTypes.includes(type) ? 'active' : ''}`, onClick: () => handleDialogueTypeToggle(type), children: type }, type))), (selectedDialogueTypes.length > 0 || dialoguesSearchTerm) && (_jsxs("button", { className: "clear-all dialogue-type-filter-button", onClick: handleClearDialoguesFilters, children: [_jsx(FaTimes, {}), " Clear"] }))] }) })] })) : selectedCategory === 'events' ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: "events-header-top", children: [getCategoryIcon(selectedCategory), _jsx("span", { className: "panel-title", children: getCategoryTitle(selectedCategory) }), _jsxs("div", { className: "search-input-wrapper", children: [_jsx(FaSearch, { className: "search-icon" }), _jsx("input", { type: "text", placeholder: "Search events...", value: eventsSearchTerm, onChange: e => handleEventsSearchChange(e.target.value), className: "search-input" }), eventsSearchTerm && (_jsx("button", { className: "clear-search-button", onClick: () => handleEventsSearchChange(''), children: _jsx(FaTimes, {}) }))] })] }), _jsx("div", { className: "visual-separator" }), _jsx("div", { className: "filter-section", children: _jsxs("div", { className: "characteristics-buttons", children: [availableCharacteristics.map(characteristic => (_jsx("button", { className: `characteristic-filter-button ${selectedEventCharacteristics.includes(characteristic) ? 'active' : ''}`, onClick: () => handleEventCharacteristicToggle(characteristic), style: {
                                                                        backgroundColor: selectedEventCharacteristics.includes(characteristic) ? ABILITY_COLORS[characteristic] : '#333',
                                                                        borderColor: ABILITY_COLORS[characteristic],
                                                                        color: selectedEventCharacteristics.includes(characteristic) ? '#fff' : ABILITY_COLORS[characteristic]
                                                                    }, children: characteristic }, characteristic))), (selectedEventCharacteristics.length > 0 || eventsSearchTerm) && (_jsxs("button", { className: "clear-all characteristic-filter-button", onClick: handleClearEventsFilters, children: [_jsx(FaTimes, {}), " Clear"] }))] }) })] })) : (_jsxs(_Fragment, { children: [getCategoryIcon(selectedCategory), _jsx("span", { className: "panel-title", children: getCategoryTitle(selectedCategory) })] })) }), selectedCategory === 'dialogues' && (_jsx("div", { className: "dialogues-list", children: getFilteredDialogues().length > 0 ? (getFilteredDialogues().map(dialogue => {
                                                var _a, _b;
                                                // Find the root NPC for the dialogue
                                                const rootNode = (_a = dialogue.nodes) === null || _a === void 0 ? void 0 : _a.find(n => dialogue.rootNodeId === n.id);
                                                const rootNPC = (rootNode === null || rootNode === void 0 ? void 0 : rootNode.selectedNPCId)
                                                    ? availableNPCs.find(npc => npc.id === rootNode.selectedNPCId)
                                                    : null;
                                                // Find related scenarios
                                                const linkedScenarios = scenarios.filter(sc => { var _a; return (_a = sc.nodes) === null || _a === void 0 ? void 0 : _a.some((n) => { var _a; return ((_a = n === null || n === void 0 ? void 0 : n.data) === null || _a === void 0 ? void 0 : _a.dialogueId) === dialogue.id; }); });
                                                // Get the first line
                                                const firstReplica = (rootNode === null || rootNode === void 0 ? void 0 : rootNode.text) || 'No dialogue content';
                                                return (_jsxs("div", { className: "dialogue-card", style: (rootNPC === null || rootNPC === void 0 ? void 0 : rootNPC.image) ? { '--npc-image': `url(${backendUrl}${rootNPC.image})` } : {}, draggable: true, onDragStart: (e) => {
                                                        e.dataTransfer.setData('application/reactflow', JSON.stringify({
                                                            type: 'dialogue',
                                                            data: dialogue
                                                        }));
                                                    }, onClick: () => handleAddDialogueToCanvas(dialogue), children: [!(rootNPC === null || rootNPC === void 0 ? void 0 : rootNPC.image) && (_jsx("div", { className: "dialogue-card-placeholder", children: _jsx(FaUser, {}) })), _jsx("div", { className: "dialogue-card-overlay", children: _jsxs("div", { className: "dialogue-card-content", children: [_jsx("h4", { className: "dialogue-title", children: dialogue.name }), _jsx("p", { className: "dialogue-description", children: firstReplica }), linkedScenarios.length > 0 && (() => {
                                                                        const firstName = linkedScenarios[0].name;
                                                                        const extra = linkedScenarios.length - 1;
                                                                        const prefix = linkedScenarios.length === 1 ? 'Scenario' : 'Scenarios';
                                                                        const mainLabel = `${prefix}: ${firstName}`;
                                                                        return (_jsxs("div", { className: "link-badge-container", children: [_jsxs("span", { className: "link-badge", "data-tooltip": mainLabel, children: [_jsx(FaLink, { className: "link-icon" }), _jsx("span", { className: "link-text", children: mainLabel })] }), extra > 0 && (() => {
                                                                                    const tooltip = linkedScenarios.slice(1).map(sc => sc.name).join(', ');
                                                                                    return (_jsxs("span", { className: "link-badge extra-count", "data-tooltip": tooltip, children: ["+", extra] }));
                                                                                })()] }));
                                                                    })(), _jsxs("div", { className: "dialogue-info-preview", children: [_jsxs("div", { className: "info-tag choices-tag", children: [_jsx(FaComment, {}), " ", ((_b = dialogue.nodes) === null || _b === void 0 ? void 0 : _b.length) || 0, " nodes"] }), rootNPC && (_jsxs("div", { className: "info-tag npc-tag", children: [_jsx(FaUser, {}), " ", rootNPC.name] }))] })] }) })] }, dialogue.id));
                                            })) : (_jsx("div", { className: "empty-dialogues-state", children: _jsx("p", { children: "No dialogues match your search criteria." }) })) })), selectedCategory === 'events' && (_jsx("div", { className: "events-list", children: getFilteredEvents().length > 0 ? (getFilteredEvents().map(event => {
                                                var _a;
                                                // Find related scenarios
                                                const linkedScenarios = scenarios.filter(sc => { var _a; return (_a = sc.nodes) === null || _a === void 0 ? void 0 : _a.some((n) => { var _a; return ((_a = n === null || n === void 0 ? void 0 : n.data) === null || _a === void 0 ? void 0 : _a.eventId) === event.id; }); });
                                                return (_jsxs("div", { className: "event-card", draggable: true, onDragStart: (e) => {
                                                        e.dataTransfer.setData('application/reactflow', JSON.stringify({
                                                            type: 'event',
                                                            data: event
                                                        }));
                                                    }, onClick: () => handleAddEventNode(event), children: [_jsx("div", { className: "event-card-background", style: {
                                                                backgroundImage: event.backgroundImage && event.backgroundImage !== '___NODE___'
                                                                    ? `url(${normalizeImageUrl(event.backgroundImage)})`
                                                                    : undefined
                                                            }, children: (event.backgroundImage === '___NODE___' || !event.backgroundImage) && (_jsx("div", { className: "event-card-node-icon", children: _jsx(FaImage, {}) })) }), _jsx("div", { className: "event-card-character", style: {
                                                                backgroundImage: event.cardImage ? `url(${normalizeImageUrl(event.cardImage)})` : undefined,
                                                            }, children: !event.cardImage && (_jsx("div", { className: "event-card-placeholder", children: _jsx(FaImage, {}) })) }), _jsx("div", { className: "event-card-overlay", children: _jsxs("div", { className: "event-card-content", children: [_jsx("h4", { className: "event-title", children: event.title }), _jsx("div", { className: "event-type-badge-container", children: _jsx("span", { className: `event-type-badge ${event.isEncounter ? 'encounter' : 'trial'}`, children: event.isEncounter ? 'Random Encounter' : 'Challenge' }) }), _jsx("p", { className: "event-description", children: event.description || 'No description' }), linkedScenarios.length > 0 && (() => {
                                                                        const firstName = linkedScenarios[0].name;
                                                                        const extra = linkedScenarios.length - 1;
                                                                        const prefix = linkedScenarios.length === 1 ? 'Scenario' : 'Scenarios';
                                                                        const mainLabel = `${prefix}: ${firstName}`;
                                                                        return (_jsxs("div", { className: "link-badge-container", children: [_jsxs("span", { className: "link-badge", "data-tooltip": mainLabel, children: [_jsx(FaLink, { className: "link-icon" }), _jsx("span", { className: "link-text", children: mainLabel })] }), extra > 0 && (() => {
                                                                                    const tooltip = linkedScenarios.slice(1).map(sc => sc.name).join(', ');
                                                                                    return (_jsxs("span", { className: "link-badge extra-count", "data-tooltip": tooltip, children: ["+", extra] }));
                                                                                })()] }));
                                                                    })(), _jsx("div", { className: "trial-options-preview", children: (_a = event.encounters) === null || _a === void 0 ? void 0 : _a.map((option, index) => (_jsxs("span", { className: "characteristic-tag", style: { backgroundColor: ABILITY_COLORS[option.ability] }, children: [option.ability, ": ", option.difficulty] }, index))) })] }) })] }, event.id));
                                            })) : (_jsx("div", { className: "empty-events-state", children: _jsx("p", { children: "No events match your search criteria." }) })) })), selectedCategory === 'ux' && (_jsxs("div", { className: "ux-list", children: [_jsxs("div", { className: "ux-item", onClick: () => handleAddUXNode('ux-panel'), children: [_jsx(FaDesktop, {}), _jsxs("div", { className: "ux-info", children: [_jsx("h4", { children: "UX Panel" }), _jsx("p", { children: "UI panel shown to players with messages" })] })] }), _jsxs("div", { className: "ux-item", onClick: () => handleAddUXNode('ux-notification'), children: [_jsx(FaBell, {}), _jsxs("div", { className: "ux-info", children: [_jsx("h4", { children: "Notification" }), _jsx("p", { children: "Small notification popup" })] })] })] })), selectedCategory === 'items' && (_jsxs("div", { className: "items-list", children: [_jsxs("div", { className: "item-item", onClick: () => handleAddItemNode('item-reward'), children: [_jsx(FaBox, {}), _jsxs("div", { className: "item-info", children: [_jsx("h4", { children: "Item Reward" }), _jsx("p", { children: "Give item to player" })] })] }), _jsxs("div", { className: "item-item", onClick: () => handleAddItemNode('item-requirement'), children: [_jsx(FaBox, {}), _jsxs("div", { className: "item-info", children: [_jsx("h4", { children: "Item Requirement" }), _jsx("p", { children: "Require item to proceed" })] })] }), _jsxs("div", { className: "item-item", onClick: () => handleAddItemNode('item-spent'), children: [_jsx(FaTrash, {}), _jsxs("div", { className: "item-info", children: [_jsx("h4", { children: "Item Spent" }), _jsx("p", { children: "Player spends/consumes items" })] })] })] })), selectedCategory === 'triggers' && (_jsx("div", { className: "triggers-list", children: _jsxs("div", { className: "trigger-item", onClick: () => handleAddTriggerNode('conditional'), children: [_jsx(FaBullseye, {}), _jsxs("div", { className: "trigger-info", children: [_jsx("h4", { children: "Conditional Trigger" }), _jsx("p", { children: "Triggers based on specific condition" })] })] }) }))] })) : currentDialogue ? (
                                // If no node or category is selected but a dialogue is selected, show the general dialogue properties
                                _jsxs("div", { className: "dialogue-info", children: [_jsx("h3", { children: "Dialogue Properties" }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "dialogueName", children: "Name:" }), _jsx("input", { id: "dialogueName", type: "text", value: (currentDialogue === null || currentDialogue === void 0 ? void 0 : currentDialogue.name) || '', onChange: evt => handleUpdateDialogueProps('name', evt.target.value) })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { children: "Character Image:" }), _jsx(ImageDropzone, { currentImageUrl: (currentDialogue === null || currentDialogue === void 0 ? void 0 : currentDialogue.characterImage) || null, onUploadSuccess: filePath => {
                                                        handleUpdateDialogueProps('characterImage', filePath);
                                                    } })] }), _jsx("p", { children: "Select a node in the dialogue flow to edit its properties." })] })) : (
                                // If nothing is selected
                                _jsx(EmptyPropertiesState, {})) }))] })] }), scenarioError && (_jsx("p", { className: "error-message", children: scenarioError })), unsavedModal, _jsx(ConfirmationModal, { isOpen: isScenarioSaveModalOpen, onClose: () => setIsScenarioSaveModalOpen(false), onConfirm: saveScenario, title: "Save Scenario", message: scenarioSaveMessage, confirmText: "Save", cancelText: "Cancel", isLoading: isSavingScenario }), _jsx(MapPickerModal, { isOpen: isMapPickerOpen, onClose: () => setIsMapPickerOpen(false), levelData: levelData, selectedNodeCode: (_l = selectedNode === null || selectedNode === void 0 ? void 0 : selectedNode.triggerMapPoint) === null || _l === void 0 ? void 0 : _l.nodeCode, onNodeSelect: (nodeCode) => {
                    var _a;
                    if (selectedNode) {
                        handleUpdateNode({
                            triggerMapPoint: {
                                nodeCode,
                                visitType: ((_a = selectedNode.triggerMapPoint) === null || _a === void 0 ? void 0 : _a.visitType) || 'OnVisit'
                            }
                        });
                    }
                } })] }));
}
export default ScenarioEditor;
